import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    FileText,
    Plus,
    Trash2,
    Download,
    Upload,
    Save,
    Maximize2,
    Settings,
    X,
    ChevronDown,
    ChevronUp,
    Book,
    Sparkles,
    ArrowRight,
    CheckCircle
} from 'lucide-react';
import { useAssessment } from './SharedContext';
import { ensureUniqueCriterionIds } from '../utils/ensureUniqueCriterionIds';
import { calculateLevelPoints } from '../utils/rubricCalculations';
import { generateReportHeader, getReportStyles, getSchoolLogo } from './logoIntegrationUtility';

const defaultLevels = [
    { level: 'incomplete', name: 'Incomplete', description: 'No submission or unusable', color: '#95a5a6', multiplier: 0, overallComment: '' },
    { level: 'unacceptable', name: 'Unacceptable', description: 'Below minimum standards', color: '#e74c3c', multiplier: 0.3, overallComment: '' },
    { level: 'developing', name: 'Developing', description: 'Approaching standards', color: '#f39c12', multiplier: 0.55, overallComment: '' },
    { level: 'acceptable', name: 'Acceptable', description: 'Meets minimum standards', color: '#27ae60', multiplier: 0.7, overallComment: '' },
    { level: 'emerging', name: 'Emerging', description: 'Above standard expectations', color: '#2980b9', multiplier: 0.82, overallComment: '' },
    { level: 'accomplished', name: 'Accomplished', description: 'Strong professional quality', color: '#16a085', multiplier: 0.92, overallComment: '' },
    { level: 'exceptional', name: 'Exceptional', description: 'Outstanding professional quality', color: '#8e44ad', multiplier: 1, overallComment: '' }
];

const normalizeOverallAchievementLevels = (incoming = [], fallbackLevels = defaultLevels) => {
    if (!Array.isArray(incoming)) return [];

    return incoming.map((level, index) => {
        const description = level.description ?? level.overallComment ?? '';
        return {
            level: level.level || fallbackLevels[index]?.level || `level_${index + 1}`,
            name: level.name || fallbackLevels[index]?.name || '',
            description,
            color: level.color || fallbackLevels[index]?.color || '#6b7280',
            multiplier: Number.isFinite(level.multiplier) ? level.multiplier : fallbackLevels[index]?.multiplier ?? 0,
            overallComment: level.overallComment ?? description
        };
    });
};

const buildLevelTemplate = (count) =>
    defaultLevels.slice(defaultLevels.length - count).map((level) => ({ ...level }));

const resizeLevels = (currentLevels, count) => {
    const template = buildLevelTemplate(count);
    const existingMap = (currentLevels || []).reduce((acc, level) => {
        acc[level.level] = level;
        return acc;
    }, {});

    return template.map((level) => ({ ...level, ...(existingMap[level.level] || {}) }));
};

const buildEmptyLevels = (levels = defaultLevels) =>
    levels.reduce((acc, level) => {
        acc[level.level] = { description: '' };
        return acc;
    }, {});

const createEmptyCriterion = (id, levels = defaultLevels) => ({
    id,
    groupId: null,
    name: '',
    description: '',
    maxPoints: 20,
    weight: 20,
    useAutoPointValues: true,
    levels: buildEmptyLevels(levels),
    feedbackLibrary: {
        strengths: [],
        improvements: [],
        general: []
    }
});

const createDefaultRubric = () => ({
    assignmentInfo: {
        title: '',
        description: '',
        weight: 25,
        passingThreshold: 60,
        totalPoints: 100
    },
    rubricLevels: [...defaultLevels],
    overallAchievementLevels: [...defaultLevels],
    groups: [],
    criteria: [createEmptyCriterion('criterion-1')]
});

const prepareRubricData = (data) => {
    if (!data) return createDefaultRubric();

    const base = createDefaultRubric();
    const safeCriteria = ensureUniqueCriterionIds(data.criteria || []);
    const safeGroups = Array.isArray(data.groups) ? data.groups : [];
    const validGroupIds = new Set(safeGroups.map((g) => g.id));
    const incomingLevels = data.rubricLevels?.length
        ? data.rubricLevels
        : data.overallAchievementLevels?.length
            ? data.overallAchievementLevels
            : [];
    const normalizedCriteria = safeCriteria.map((criterion) => ({
        ...criterion,
        groupId: criterion.groupId && validGroupIds.has(criterion.groupId) ? criterion.groupId : null
    }));

    const normalizedOverallLevels = data.overallAchievementLevels?.length ? data.overallAchievementLevels : incomingLevels;

    return {
        ...base,
        ...data,
        groups: safeGroups,
        rubricLevels: incomingLevels.length ? incomingLevels : base.rubricLevels,
        overallAchievementLevels: normalizedOverallLevels?.length ? normalizedOverallLevels : base.overallAchievementLevels,
        criteria: normalizedCriteria.length ? normalizedCriteria : base.criteria
    };
};

const normalizeRubricData = (data) => {
    const prepared = prepareRubricData(data);

    const normalizedGroups = (prepared.groups || []).map((group, index) => ({
        id: group.id || `group_${index}`,
        name: group.name || '',
        description: group.description || '',
        showTotals: group.showTotals !== false,
        collapseByDefault: group.collapseByDefault || false,
        order: group.order ?? index,
        levels: (group.levels?.length ? group.levels : prepared.rubricLevels).map((lvl) => ({ ...lvl }))
    }));

    const validGroupIds = new Set(normalizedGroups.map((g) => g.id));

    const overallAchievementLevels = normalizeOverallAchievementLevels(
        prepared.overallAchievementLevels?.length ? prepared.overallAchievementLevels : prepared.rubricLevels,
        prepared.rubricLevels?.length ? prepared.rubricLevels : defaultLevels
    );

    const normalizedCriteria = (prepared.criteria || []).map((criterion, index) => {
        const groupLevels = normalizedGroups.find((group) => group.id === criterion.groupId)?.levels || prepared.rubricLevels;
        const groupLevelMap = groupLevels.reduce((acc, level) => {
            acc[level.level] = { description: '' };
            return acc;
        }, {});
        const mergedLevels = { ...groupLevelMap };
        Object.entries(criterion.levels || {}).forEach(([levelKey, levelData]) => {
            mergedLevels[levelKey] = { ...(mergedLevels[levelKey] || { description: '' }), ...levelData };
        });

        const feedback = {
            strengths: [],
            improvements: [],
            general: [],
            ...(criterion.feedbackLibrary || {})
        };

        return {
            id: criterion.id || `criterion_${index}`,
            name: criterion.name || '',
            description: criterion.description || '',
            maxPoints: Number(criterion.maxPoints) || 0,
            weight: Number(criterion.weight) || 0,
            useAutoPointValues: criterion.useAutoPointValues !== false,
            groupId: criterion.groupId && validGroupIds.has(criterion.groupId) ? criterion.groupId : null,
            levels: mergedLevels,
            feedbackLibrary: {
                strengths: Array.isArray(feedback.strengths) ? feedback.strengths : [],
                improvements: Array.isArray(feedback.improvements) ? feedback.improvements : [],
                general: Array.isArray(feedback.general) ? feedback.general : []
            }
        };
    });

    return {
        ...prepared,
        groups: normalizedGroups,
        criteria: normalizedCriteria,
        overallAchievementLevels
    };
};

const RenderLevelCellContent = React.memo(
    ({
        level,
        useAutoPointValues,
        levelData,
        autoPoints,
        onPointsChange,
        onDescriptionChange
    }) => (
        <div className="p-3">
            {!useAutoPointValues && (
                <div className="mb-2 flex items-center gap-2">
                    <label className="text-xs text-gray-700 font-semibold">Points</label>
                    <input
                        type="number"
                        value={levelData?.pointRange ?? ''}
                        onChange={(e) => onPointsChange(level.level, e.target.value)}
                        className="w-24 px-2 py-1 text-xs border rounded"
                        placeholder={String(autoPoints)}
                    />
                    <span className="text-[11px] text-gray-500">auto: {autoPoints}</span>
                </div>
            )}
            <textarea
                value={levelData?.description || ''}
                onChange={(e) => onDescriptionChange(level.level, e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                rows="2"
                placeholder={`Describe ${level.name.toLowerCase()}...`}
            />
        </div>
    )
);

const CriterionLevelCell = React.memo(
    ({ level, useAutoPointValues, levelData, autoPoints, onPointsChange, onDescriptionChange }) => (
        <div className="border rounded-lg overflow-hidden">
            <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: level.color + '15' }}>
                <span className="font-semibold text-sm" style={{ color: level.color }}>
                    {level.name}
                </span>
                <span
                    className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: level.color + '20', color: level.color }}
                >
                    {autoPoints} pts
                </span>
            </div>
            <RenderLevelCellContent
                level={level}
                useAutoPointValues={useAutoPointValues}
                levelData={levelData}
                autoPoints={autoPoints}
                onPointsChange={onPointsChange}
                onDescriptionChange={onDescriptionChange}
            />
        </div>
    )
);

const CriterionItem = React.memo(({ criterion, index = 0, isActive, onSelect, onDelete }) => (
    <div
        className={`criterion-item ${isActive ? 'active' : ''}`}
        onClick={() => onSelect(criterion.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect?.(criterion.id)}
    >
        <span className="criterion-drag">⋮⋮</span>
        <span className="criterion-number">#{index + 1}</span>
        <span className="criterion-name">{criterion.name || 'Untitled Criterion'}</span>
        <span className="criterion-points">{criterion.maxPoints || 0} pts</span>
        <button
            className="icon-btn-sm danger criterion-delete-btn"
            onClick={(e) => {
                e.stopPropagation();
                onDelete?.(criterion.id);
            }}
            aria-label="Delete criterion"
        >
            <X size={14} />
        </button>
        <span className="criterion-nav">►</span>
    </div>
));

const GroupWrapper = React.memo(
    ({
        group,
        criteria,
        isActive,
        isExpanded,
        onToggleExpand,
        onSelectGroup,
        onEditGroup,
        onRenameGroup,
        onDeleteGroup,
        onDeleteCriterion,
        activeCriterionId,
        onSelectCriterion
    }) => {
        const totalPointsForGroup = criteria.reduce((sum, criterion) => sum + (criterion.maxPoints || 0), 0);

        return (
            <div className={`group-wrapper ${isActive ? 'has-active' : ''} ${!isExpanded ? 'collapsed' : ''}`}>
                <div className="group-header-bar" onClick={() => onToggleExpand(group.id)}>
                    <span className="drag-handle">⋮⋮</span>
                    <span className="group-icon">📁</span>
                    <div className="group-info">
                        <input
                            className="group-name-input bg-transparent border-none focus:ring-0 focus:outline-none font-semibold text-left w-full"
                            value={group.name || ''}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => onRenameGroup?.(group.id, e.target.value)}
                            placeholder="Untitled Group"
                        />
                        <div className="group-stats">{criteria.length} criteria · {totalPointsForGroup} pts</div>
                    </div>
                    <span className="collapse-icon">▼</span>
                    <div className="group-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="icon-btn-sm" onClick={() => onEditGroup(group.id)} aria-label="Edit group">
                            ✏️
                        </button>
                        <button className="icon-btn-sm danger" onClick={() => onDeleteGroup(group.id)} aria-label="Delete group">
                            🗑️
                        </button>
                    </div>
                </div>

                {isExpanded && (
                    <div className="group-criteria">
                        {criteria.map((criterion, index) => (
                            <CriterionItem
                                key={criterion.id}
                                criterion={criterion}
                                index={index}
                                isActive={activeCriterionId === criterion.id}
                                onDelete={onDeleteCriterion}
                                onSelect={(id) => {
                                    onSelectGroup?.(group.id);
                                    onSelectCriterion?.(id);
                                }}
                            />
                        ))}
                        {criteria.length === 0 && (
                            <div className="p-3 text-xs text-gray-500">No criteria in this group yet.</div>
                        )}
                    </div>
                )}
            </div>
        );
    }
);

const FeedbackSection = React.memo(({ title, icon, color, items, onAdd, onRemove, onUpdate }) => {
    const [newItem, setNewItem] = useState('');
    const [isExpanded, setIsExpanded] = useState(true);

    const handleAdd = () => {
        if (newItem.trim()) {
            onAdd(newItem.trim());
            setNewItem('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            handleAdd();
        }
    };

    return (
        <div className="border rounded-lg overflow-hidden" style={{ borderColor: color + '40' }}>
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-3 py-2 flex items-center justify-between text-sm font-semibold"
                style={{ backgroundColor: color + '15', color: color }}
            >
                <span className="flex items-center gap-2">
                    <span>{icon}</span>
                    {title}
                </span>
                <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-white/50">{items.length}</span>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
            </button>

            {isExpanded && (
                <div className="p-3 space-y-2">
                    <div className="flex gap-2">
                        <textarea
                            value={newItem}
                            onChange={(e) => setNewItem(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="flex-1 px-2 py-2 text-xs border border-gray-200 rounded focus:ring-2 focus:outline-none resize-y min-h-[56px]"
                            placeholder="Add comment..."
                        />
                        <button
                            onClick={handleAdd}
                            className="px-3 py-1 text-white rounded text-xs hover:opacity-90 transition-all"
                            style={{ backgroundColor: color }}
                        >
                            <Plus size={14} />
                        </button>
                    </div>

                    <div className="space-y-1">
                        {items.map((item, index) => (
                            <div
                                key={`${title}-${index}`}
                                className="flex items-start gap-2 p-2 bg-gray-50 rounded text-xs group hover:bg-gray-100 transition-all"
                            >
                                <textarea
                                    value={item}
                                    onChange={(e) => onUpdate?.(index, e.target.value)}
                                    className="flex-1 px-2 py-1 border border-gray-200 rounded resize-y min-h-[48px] focus:ring-2 focus:outline-none"
                                />
                                <button
                                    onClick={() => onRemove(index)}
                                    className="opacity-0 group-hover:opacity-100 text-red-600 hover:bg-red-50 p-1 rounded transition-all"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        ))}
                        {items.length === 0 && <p className="text-xs text-gray-400 italic text-center py-2">No comments yet</p>}
                    </div>
                </div>
            )}
        </div>
    );
});

const RubricCreator = () => {
    const {
        sharedRubric,
        setSharedRubric,
        transferRubricToGradingWithDetails,
        setActiveTab,
        classList,
        initializeGradingSession,
        activeTab
    } = useAssessment();

    const [rubricData, setRubricData] = useState(() => prepareRubricData(sharedRubric));
    const [expandedSections, setExpandedSections] = useState({ assignment: true, levels: false, criteria: true });
    const [activeCriterion, setActiveCriterion] = useState(() => (sharedRubric?.criteria?.[0]?.id) || 'criterion-1');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [activeGroup, setActiveGroup] = useState(null);
    const [activeGroupManuallySelected, setActiveGroupManuallySelected] = useState(false);
    const [isGroupModalOpen, setGroupModalOpen] = useState(false);
    const [groupModalDraft, setGroupModalDraft] = useState(null);
    const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
    const [pasteAccordionOpen, setPasteAccordionOpen] = useState(false);
    const [pasteJSONText, setPasteJSONText] = useState('');
    const fileInputRef = useRef(null);
    const feedbackFileInputRef = useRef(null);
    const overallAchievementFileInputRef = useRef(null);

    // Ref to track latest rubric data for unmount cleanup
    const latestRubricDataRef = useRef(rubricData);
    useEffect(() => { latestRubricDataRef.current = rubricData; }, [rubricData]);

    // Flush debounced save to SharedContext on unmount (safety net)
    useEffect(() => {
        return () => {
            if (latestRubricDataRef.current) {
                setSharedRubric(latestRubricDataRef.current);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalPoints = useMemo(() => rubricData.criteria.reduce((sum, criterion) => sum + (Number(criterion.maxPoints) || 0), 0), [rubricData.criteria]);

    const groupedCriteriaMap = useMemo(() => {
        const map = {};
        rubricData.groups.forEach((group) => {
            map[group.id] = rubricData.criteria.filter((criterion) => criterion.groupId === group.id);
        });
        map.__ungrouped = rubricData.criteria.filter((criterion) => !criterion.groupId);
        return map;
    }, [rubricData.criteria, rubricData.groups]);

    const getCriteriaForGroup = useCallback((groupId) => groupedCriteriaMap[groupId] || [], [groupedCriteriaMap]);

    const getUngroupedCriteria = useCallback(() => groupedCriteriaMap.__ungrouped || [], [groupedCriteriaMap]);

    const selectCriterion = useCallback((criterionId) => {
        setActiveGroupManuallySelected(false);
        setActiveCriterion(criterionId);
    }, []);

    useEffect(() => {
        if (autoSaveTimeout) {
            clearTimeout(autoSaveTimeout);
        }

        const timeoutId = setTimeout(() => {
            setSharedRubric(rubricData);
        }, 400);

        setAutoSaveTimeout(timeoutId);
        return () => clearTimeout(timeoutId);
    }, [rubricData, setSharedRubric]);

    useEffect(() => {
        if (sharedRubric && activeTab === 'rubric-creator') {
            const prepared = prepareRubricData(sharedRubric);
            setRubricData(prepared);

            const stillExists = prepared.criteria?.some((c) => c.id === activeCriterion);
            if (!stillExists && prepared.criteria?.length) {
                selectCriterion(prepared.criteria[0].id);
            }
        }
    }, [sharedRubric, activeTab, activeCriterion, selectCriterion]);

    useEffect(() => {
        if (!rubricData.criteria.find((c) => c.id === activeCriterion) && rubricData.criteria.length > 0) {
            selectCriterion(rubricData.criteria[0].id);
        }
    }, [rubricData.criteria, activeCriterion, selectCriterion]);

    useEffect(() => {
        const activeCriterionData = rubricData.criteria.find((c) => c.id === activeCriterion);
        if (activeCriterionData?.groupId && !expandedGroups[activeCriterionData.groupId]) {
            setExpandedGroups((prev) => ({
                ...prev,
                [activeCriterionData.groupId]: true
            }));
        }
        if (!activeGroupManuallySelected || activeCriterionData?.groupId === activeGroup) {
            const derivedGroup = activeCriterionData?.groupId || null;
            setActiveGroup((current) => (current === derivedGroup ? current : derivedGroup));
        }
    }, [activeCriterion, rubricData.criteria, expandedGroups, activeGroupManuallySelected, activeGroup]);

    const toggleSection = useCallback((section) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    }, []);

    const reverseRubricOrder = useCallback(() => {
        setRubricData((prev) => ({ ...prev, rubricLevels: [...prev.rubricLevels].reverse() }));
        setSharedRubric((prev) => (prev ? { ...prev, rubricLevels: [...(prev.rubricLevels || [])].reverse() } : null));
    }, [setSharedRubric]);

    const calculateTotalPoints = useCallback(() => totalPoints, [totalPoints]);

    const hasActiveCriterion = useCallback(
        (groupId) => rubricData.criteria.some((c) => c.groupId === groupId && c.id === activeCriterion),
        [activeCriterion, rubricData.criteria]
    );

    const getGroupLevels = useCallback(
        (groupId) => {
            const group = rubricData.groups.find((item) => item.id === groupId);
            return group?.levels?.length ? group.levels : rubricData.rubricLevels;
        },
        [rubricData.groups, rubricData.rubricLevels]
    );

    const addNewGroup = useCallback(() => {
        const newGroup = {
            id: `group-${Date.now()}`,
            name: '',
            description: '',
            showTotals: true,
            collapseByDefault: false,
            order: rubricData.groups.length,
            levels: rubricData.rubricLevels.map((lvl) => ({ ...lvl }))
        };

        setRubricData((prev) => ({
            ...prev,
            groups: [...prev.groups, newGroup]
        }));
        setActiveGroup(newGroup.id);
        setActiveGroupManuallySelected(true);
        setExpandedGroups((prev) => ({ ...prev, [newGroup.id]: true }));
    }, [rubricData.groups.length]);

    const handleEditGroup = useCallback(
        (groupId) => {
            const targetGroup = rubricData.groups.find((group) => group.id === groupId);
            if (!targetGroup) return;

            setActiveGroup(groupId);
            setActiveGroupManuallySelected(true);
            setExpandedGroups((prev) => ({ ...prev, [groupId]: true }));
            setGroupModalDraft({
                ...targetGroup,
                levels: targetGroup.levels?.length ? targetGroup.levels : rubricData.rubricLevels
            });
            setGroupModalOpen(true);
        },
        [rubricData.groups, rubricData.rubricLevels]
    );

    const updateGroup = useCallback((groupId, field, value) => {
        setRubricData((prev) => ({
            ...prev,
            groups: prev.groups.map((group) => (group.id === groupId ? { ...group, [field]: value } : group))
        }));
    }, []);

    const handleRenameGroup = useCallback(
        (groupId, name) => {
            updateGroup(groupId, 'name', name);
            setGroupModalDraft((prev) => (prev?.id === groupId ? { ...prev, name } : prev));
        },
        [updateGroup]
    );

    const handleGroupDraftChange = useCallback((field, value) => {
        setGroupModalDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    }, []);

    const handleGroupLevelCountChange = useCallback((count) => {
        setGroupModalDraft((prev) => (prev ? { ...prev, levels: resizeLevels(prev.levels, count) } : prev));
    }, []);

    const handleSaveGroupDetails = useCallback(() => {
        if (!groupModalDraft) return;

        setRubricData((prev) => {
            const nextLevels = groupModalDraft.levels?.length ? groupModalDraft.levels : prev.rubricLevels;
            const updatedGroups = prev.groups.map((group) =>
                group.id === groupModalDraft.id ? { ...group, ...groupModalDraft, levels: nextLevels } : group
            );

            const updatedCriteria = prev.criteria.map((criterion) => {
                if (criterion.groupId !== groupModalDraft.id) return criterion;
                const existingLevels = criterion.levels || {};
                const syncedLevels = nextLevels.reduce((acc, level) => {
                    acc[level.level] = existingLevels[level.level] || { description: '' };
                    return acc;
                }, {});
                return { ...criterion, levels: syncedLevels };
            });

            return {
                ...prev,
                groups: updatedGroups,
                criteria: updatedCriteria
            };
        });
        setGroupModalOpen(false);
        setGroupModalDraft(null);
    }, [groupModalDraft]);

    const handleCloseGroupModal = useCallback(() => {
        setGroupModalOpen(false);
        setGroupModalDraft(null);
    }, []);

    const deleteGroup = useCallback((groupId) => {
        setRubricData((prev) => ({
            ...prev,
            groups: prev.groups.filter((group) => group.id !== groupId),
            criteria: prev.criteria.map((criterion) =>
                criterion.groupId === groupId ? { ...criterion, groupId: null } : criterion
            )
        }));

        if (activeGroup === groupId) {
            setActiveGroup(null);
            setActiveGroupManuallySelected(false);
        }
    }, [activeGroup]);

    const toggleGroupExpand = useCallback((groupId) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    }, []);

    const calculateGroupPoints = (groupId) =>
        getCriteriaForGroup(groupId).reduce((sum, criterion) => sum + (criterion.maxPoints || 0), 0);

    const calculateGroupPercentage = (groupId) => {
        const groupPoints = calculateGroupPoints(groupId);
        const totalRubricPoints = rubricData.criteria.reduce(
            (sum, criterion) => sum + (criterion.maxPoints || 0),
            0
        );
        return totalRubricPoints > 0 ? ((groupPoints / totalRubricPoints) * 100).toFixed(1) : 0;
    };

    const handleGroupToggleExpand = useCallback(
        (groupId) => {
            setActiveGroup(groupId);
            setActiveGroupManuallySelected(true);
            toggleGroupExpand(groupId);
        },
        [toggleGroupExpand]
    );

    const handleGroupSelect = useCallback((groupId) => {
        setActiveGroup(groupId);
        setActiveGroupManuallySelected(true);
    }, []);

    const getCurrentCriterionIndex = () => rubricData.criteria.findIndex((criterion) => criterion.id === activeCriterion);

    const hasPreviousCriterion = () => getCurrentCriterionIndex() > 0;

    const hasNextCriterion = () => getCurrentCriterionIndex() < rubricData.criteria.length - 1;

    const goToPreviousCriterion = () => {
        const currentIndex = getCurrentCriterionIndex();
        if (currentIndex > 0) {
            const prevCriterion = rubricData.criteria[currentIndex - 1];
            selectCriterion(prevCriterion.id);
            if (prevCriterion.groupId && !expandedGroups[prevCriterion.groupId]) {
                setExpandedGroups((prev) => ({ ...prev, [prevCriterion.groupId]: true }));
            }
        }
    };

    const goToNextCriterion = () => {
        const currentIndex = getCurrentCriterionIndex();
        if (currentIndex < rubricData.criteria.length - 1) {
            const nextCriterion = rubricData.criteria[currentIndex + 1];
            selectCriterion(nextCriterion.id);
            if (nextCriterion.groupId && !expandedGroups[nextCriterion.groupId]) {
                setExpandedGroups((prev) => ({ ...prev, [nextCriterion.groupId]: true }));
            }
        }
    };

    const handleCriterionSelect = useCallback(
        (criterionId) => {
            selectCriterion(criterionId);
        },
        [selectCriterion]
    );

    const handleUngroupedCriterionSelect = useCallback(
        (criterionId) => {
            selectCriterion(criterionId);
            setActiveGroup(null);
        },
        [selectCriterion]
    );

    const hexToRgba = (hex, alpha) => {
        try {
            // Handle undefined, null, or invalid hex values
            if (!hex || typeof hex !== 'string' || hex.trim() === '') {
                return `rgba(200, 200, 200, ${alpha})`; // Default gray color
            }
            const sanitized = String(hex).replace('#', '');
            if (!sanitized || sanitized.length === 0) {
                return `rgba(200, 200, 200, ${alpha})`; // Default gray color
            }
            const bigint = parseInt(sanitized, 16);
            // Check if parsing was successful
            if (isNaN(bigint) || !Number.isFinite(bigint)) {
                return `rgba(200, 200, 200, ${alpha})`; // Default gray color
            }
            const r = (bigint >> 16) & 255;
            const g = (bigint >> 8) & 255;
            const b = bigint & 255;
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        } catch (error) {
            console.warn('Error in hexToRgba:', error, 'hex:', hex);
            return `rgba(200, 200, 200, ${alpha})`; // Default gray color on any error
        }
    };

    const getLevelPointsForCriterion = (criterion, levelKey, overrideLevels) =>
        calculateLevelPoints({
            criterion,
            levelKey,
            rubricLevels: overrideLevels || getGroupLevels(criterion.groupId)
        });

    const buildRubricHTMLContent = () => {
        try {
            const generatedAt = new Date();
            const totalPointsValue = calculateTotalPoints();
            const levelStyleMap = new Map();
            const registerLevelStyle = (level) => {
                if (!level?.level) return;
                if (!levelStyleMap.has(level.level)) {
                    // Ensure level has required properties
                    const safeLevel = {
                        ...level,
                        color: level.color || '#94a3b8',
                        name: level.name || level.level,
                        multiplier: typeof level.multiplier === 'number' ? level.multiplier : 0
                    };
                    levelStyleMap.set(level.level, safeLevel);
                }
            };

            const overallLevels = rubricData.overallAchievementLevels?.length
                ? rubricData.overallAchievementLevels
                : rubricData.rubricLevels;

            // Filter out any null/undefined levels before registering
            (rubricData.rubricLevels || []).filter(level => level && level.level).forEach(registerLevelStyle);
            (rubricData.groups || []).forEach((group) =>
                (group?.levels || []).filter(level => level && level.level).forEach(registerLevelStyle)
            );
            (overallLevels || []).filter(level => level && level.level).forEach(registerLevelStyle);

        const dynamicLevelStyles = Array.from(levelStyleMap.values())
            .filter(level => level && level.level) // Filter out any invalid levels
            .map(
                (level) => {
                    const levelColor = level.color || '#94a3b8'; // Default gray-blue color
                    return `
        .level-${level.level} {
            background: ${hexToRgba(levelColor, 0.1)};
            border-left: 4px solid ${levelColor};
        }
        .achievement-${level.level} {
            background: ${hexToRgba(levelColor, 0.1)};
            border: 2px solid ${levelColor};
            color: ${levelColor};
        }
        `;
                }
            )
            .join('\n');

        const renderGroupTable = ({ title, description, showTotals, criteria, levels }) => {
            if (!criteria?.length) return '';
            const levelCount = levels?.length || 0;
            const levelWidth = levelCount ? (85 / levelCount).toFixed(6) : 85;
            const sampleCriterion = criteria[0];

            return `
        <table class="rubric-table" style="margin-top: 20px;">
            <thead>
                <tr class="group-header-row">
                    <th colspan="${levelCount + 1}" class="group-header-cell" style="text-align: left;">
                        <strong>${title}</strong>
                        ${showTotals ? `<span class="group-total">(${criteria.reduce((sum, c) => sum + (c.maxPoints || 0), 0)} points)</span>` : ''}
                        ${description ? `<div class="group-description">${description}</div>` : ''}
                    </th>
                </tr>
                <tr>
                    <th style="width: 15%;">Criterion</th>
                    ${levels
                        .map(
                            (level) => `
                            <th style="width: ${levelWidth}%;">
                                    <div style="font-weight: bold; font-size: 1.1em;">${level.name}</div>
                                    <div style="font-size: 0.9em; font-weight: normal; margin-top: 4px;">(${getLevelPointsForCriterion(sampleCriterion, level.level, levels)} pts)</div>
                                </th>`
                        )
                        .join('')}
                </tr>
            </thead>
            <tbody>
                ${criteria
                    .map(
                        (criterion) => `
                            <tr>
                                <td class="criterion-header">
                                    <strong>${criterion.name || 'Untitled Criterion'}</strong>
                                    <div style="font-size: 0.8em; color: #64748b; margin-top: 5px;">
                                        ${criterion.maxPoints || 0} points
                                    </div>
                                    <div style="font-size: 0.85em; margin-top: 8px; color: #475569;">${criterion.description || ''}</div>
                                </td>
                                ${levels
                                    .map(
                                        (level) => `
                                            <td class="level-${level.level}">
                                                <div class="points" style="margin-bottom: 8px; font-size: 1.1em;">
                                                    ${getLevelPointsForCriterion(criterion, level.level, levels)} pts
                                                </div>
                                                <div style="font-size: 0.9em; line-height: 1.4;">
                                                    ${criterion.levels?.[level.level]?.description || 'No description provided.'}
                                                </div>
                                            </td>
                                        `
                                    )
                                    .join('')}
                            </tr>
                        `
                    )
                    .join('')}
            </tbody>
        </table>`;
        };

        const groupTables = rubricData.groups
            .map((group) =>
                renderGroupTable({
                    title: `📁 ${group.name || 'Untitled Group'}`,
                    description: group.description,
                    showTotals: group.showTotals,
                    criteria: getCriteriaForGroup(group.id),
                    levels: getGroupLevels(group.id)
                })
            )
            .join('');

        const ungroupedTable = renderGroupTable({
            title: '⊗ Ungrouped Criteria',
            description: '',
            showTotals: false,
            criteria: getUngroupedCriteria(),
            levels: rubricData.rubricLevels
        });

        // Generate header with logo integration
        const reportData = {
            title: `${rubricData.assignmentInfo.title || 'Assessment'} - Rubric`,
            courseCode: '',
            courseName: '',
            section: '',
            rubricTitle: rubricData.assignmentInfo.title || 'Assessment Rubric'
        };

        return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${rubricData.assignmentInfo.title || 'Rubric'} - Rubric</title>
    ${getReportStyles()}
    <style>
        body { font-family: 'Arial', sans-serif; max-width: 1200px; margin: 0 auto; padding: 20px; line-height: 1.4; }
        .assignment-info { background: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 25px; border: 1px solid #e2e8f0; }
        .rubric-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; font-size: 0.85rem; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
        .rubric-table th, .rubric-table td { border: 1px solid #cbd5e1; padding: 12px; text-align: left; vertical-align: top; }
        .rubric-table th { background: linear-gradient(135deg, #1e40af, #3b82f6); color: white; font-weight: 600; }
        .criterion-header { background: #f1f5f9; font-weight: 600; color: #334155; }

        /* Dynamic level-specific CSS based on actual rubric data */
        ${dynamicLevelStyles}

        .points { font-weight: 600; color: #059669; text-align: center; }
        .group-header-row { background: linear-gradient(135deg, #f8fafc, #f1f5f9); }
        .group-header-cell { padding: 15px 20px; font-size: 1.1em; color: #1e3a8a; border-bottom: 2px solid #3b82f6; }
        .group-total { margin-left: 10px; font-size: 0.9em; color: #059669; font-weight: 600; }
        .group-description { margin-top: 5px; font-size: 0.85em; color: #64748b; font-weight: normal; }

        @media print {
            body { font-size: 10pt; background: white; }
            .school-logo, .report-logo, .assignment-logo { max-height: 40px !important; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            .achievement-grid { grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)) !important; }
        }
    </style>
</head>
<body>
    ${generateReportHeader(reportData, { maxHeight: 80, maxWidth: 300 })}

    <div class="assignment-info">
        <h2 style="margin: 0 0 15px 0; color: #1e40af;">Assignment Information</h2>
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; font-size: 0.95rem;">
            <div><strong>Weight:</strong> ${rubricData.assignmentInfo.weight || 0}%</div>
            <div><strong>Total Points:</strong> ${totalPointsValue}</div>
            <div><strong>Passing Threshold:</strong> ${rubricData.assignmentInfo.passingThreshold || 0}%</div>
        </div>
        <div style="margin-top: 15px;"><strong>Description:</strong><br>${rubricData.assignmentInfo.description || ''}</div>
    </div>

    ${groupTables}${ungroupedTable}

    
    <div class="achievement-levels-section" style="margin-bottom: 30px;">
        <h2 style="color: #1e40af; margin-bottom: 20px; font-size: 1.5em;">Overall Achievement Levels</h2>
        <p style="color: #64748b; margin-bottom: 20px; font-size: 0.95em;">
            Students receive an overall achievement level based on their total points across all criteria.
        </p>
        <div class="achievement-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
            ${overallLevels
                .map(
                    (level) => `
                <div class="achievement-${level.level}" style="
                    padding: 20px;
                    border-radius: 12px;
                    text-align: center; 
                    font-weight: 600;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                ">
                    <div style="font-size: 1.1em; margin-bottom: 8px;">${level.name}</div>
                    <div style="font-size: 1.8em; font-weight: bold; margin-bottom: 5px;">${Math.round((level.multiplier || 0) * totalPointsValue)}+ pts</div>
                    <div style="font-size: 0.9em; opacity: 0.8;">${((level.multiplier || 0) * 100).toFixed(0)}%+ required</div>
                    <div style="font-size: 0.85em; margin-top: 8px; opacity: 0.9; line-height: 1.4;">${level.description ?? level.overallComment ?? 'No description provided.'}</div>
                </div>
                `
                )
                .join('')}
        </div>
    </div>

    <div class="footer">
        <p><strong>Assessment Rubric</strong></p>
        <p>Generated on ${generatedAt.toLocaleDateString()} at ${generatedAt.toLocaleTimeString()}.</p>

    </div>
</body>
</html>`;
        } catch (error) {
            console.error('Error building HTML content:', error);
            throw new Error(`Failed to build HTML content: ${error.message}`);
        }
    };

    const buildRubricPDFContent = () => {
        try {
            const generatedAt = new Date();
            const totalPointsValue = calculateTotalPoints();
            const levelStyleMap = new Map();
            const registerLevelStyle = (level) => {
                if (!level?.level) return;
                if (!levelStyleMap.has(level.level)) {
                    const safeLevel = {
                        ...level,
                        color: level.color || '#94a3b8',
                        name: level.name || level.level,
                        multiplier: typeof level.multiplier === 'number' ? level.multiplier : 0
                    };
                    levelStyleMap.set(level.level, safeLevel);
                }
            };

            const overallLevels = rubricData.overallAchievementLevels?.length
                ? rubricData.overallAchievementLevels
                : rubricData.rubricLevels;

            // Filter out any null/undefined levels before registering
            (rubricData.rubricLevels || []).filter(level => level && level.level).forEach(registerLevelStyle);
            (rubricData.groups || []).forEach((group) =>
                (group?.levels || []).filter(level => level && level.level).forEach(registerLevelStyle)
            );
            (overallLevels || []).filter(level => level && level.level).forEach(registerLevelStyle);

            const dynamicLevelStyles = Array.from(levelStyleMap.values())
                .map(
                    (level) => {
                        const levelColor = level.color || '#94a3b8';
                        return `
        .level-${level.level} {
            background: ${hexToRgba(levelColor, 0.08)};
            border-left: 3px solid ${levelColor};
        }
        .achievement-${level.level} {
            background: ${hexToRgba(levelColor, 0.08)};
            border: 1.5px solid ${levelColor};
            color: ${levelColor};
        }
        `;
                    }
                )
                .join('\n');

            const renderGroupTable = ({ title, description, showTotals, criteria, levels }) => {
                if (!criteria?.length) return '';
                const levelCount = levels?.length || 0;
                const levelWidth = levelCount ? (85 / levelCount).toFixed(6) : 85;
                const sampleCriterion = criteria[0];

                return `
        <div class="table-section">
            <table class="rubric-table">
                <thead>
                    <tr class="group-header-row">
                        <th colspan="${levelCount + 1}" class="group-header-cell">
                            <strong>${title}</strong>
                            ${showTotals ? `<span class="group-total">(${criteria.reduce((sum, c) => sum + (c.maxPoints || 0), 0)} pts)</span>` : ''}
                            ${description ? `<div class="group-description">${description}</div>` : ''}
                        </th>
                    </tr>
                    <tr>
                        <th style="width: 15%;">Criterion</th>
                        ${levels
                            .map(
                                (level) => `
                                <th style="width: ${levelWidth}%;">
                                    <div style="font-weight: bold; font-size: 0.95em;">${level.name}</div>
                                    <div style="font-size: 0.8em; font-weight: normal; margin-top: 2px;">(${getLevelPointsForCriterion(sampleCriterion, level.level, levels)} pts)</div>
                                </th>`
                            )
                            .join('')}
                    </tr>
                </thead>
                <tbody>
                    ${criteria
                        .map(
                            (criterion) => `
                                <tr class="criterion-row">
                                    <td class="criterion-header">
                                        <strong>${criterion.name || 'Untitled Criterion'}</strong>
                                        <div style="font-size: 0.75em; color: #64748b; margin-top: 3px;">
                                            ${criterion.maxPoints || 0} points
                                        </div>
                                        ${criterion.description ? `<div style="font-size: 0.8em; margin-top: 5px; color: #475569;">${criterion.description}</div>` : ''}
                                    </td>
                                    ${levels
                                        .map(
                                            (level) => `
                                                <td class="level-${level.level}">
                                                    <div class="points" style="margin-bottom: 5px; font-size: 0.95em;">
                                                        ${getLevelPointsForCriterion(criterion, level.level, levels)} pts
                                                    </div>
                                                    <div style="font-size: 0.8em; line-height: 1.3;">
                                                        ${criterion.levels?.[level.level]?.description || 'No description provided.'}
                                                    </div>
                                                </td>
                                            `
                                        )
                                        .join('')}
                                </tr>
                            `
                        )
                        .join('')}
                </tbody>
            </table>
        </div>`;
            };

            const groupTables = rubricData.groups
                .map((group) =>
                    renderGroupTable({
                        title: `📁 ${group.name || 'Untitled Group'}`,
                        description: group.description,
                        showTotals: group.showTotals,
                        criteria: getCriteriaForGroup(group.id),
                        levels: getGroupLevels(group.id)
                    })
                )
                .join('');

            const ungroupedTable = renderGroupTable({
                title: '⊗ Ungrouped Criteria',
                description: '',
                showTotals: false,
                criteria: getUngroupedCriteria(),
                levels: rubricData.rubricLevels
            });

            const reportData = {
                title: `${rubricData.assignmentInfo.title || 'Assessment'} - Rubric`,
                courseCode: '',
                courseName: '',
                section: '',
                rubricTitle: rubricData.assignmentInfo.title || 'Assessment Rubric'
            };

            return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${rubricData.assignmentInfo.title || 'Rubric'} - Rubric</title>
    <style>
        @page {
            size: letter;
            margin: 0.5in 0.4in;
        }

        * {
            box-sizing: border-box;
        }

        body {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 10px;
            font-size: 9pt;
            line-height: 1.3;
            color: #333;
        }

        .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #1e3a8a;
            page-break-after: avoid;
            page-break-inside: avoid;
        }

        .header-content h1 {
            color: #1e3a8a;
            margin: 0 0 5px 0;
            font-size: 16pt;
        }

        .header .meta {
            color: #666;
            font-size: 8pt;
            line-height: 1.4;
        }

        .report-logo {
            max-height: 50px;
            max-width: 200px;
            object-fit: contain;
        }

        .assignment-info {
            background: #f8fafc;
            padding: 10px 12px;
            margin-bottom: 12px;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            page-break-inside: avoid;
            page-break-after: avoid;
        }

        .assignment-info h2 {
            margin: 0 0 8px 0;
            color: #1e40af;
            font-size: 11pt;
        }

        .assignment-info .info-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
            font-size: 8.5pt;
            margin-bottom: 8px;
        }

        .assignment-info .description {
            margin-top: 8px;
            font-size: 8.5pt;
        }

        .table-section {
            page-break-inside: avoid;
            margin-bottom: 15px;
        }

        .rubric-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8pt;
        }

        .rubric-table th,
        .rubric-table td {
            border: 1px solid #cbd5e1;
            padding: 6px 8px;
            text-align: left;
            vertical-align: top;
        }

        .rubric-table th {
            background: #1e40af;
            color: white;
            font-weight: 600;
            font-size: 8.5pt;
        }

        .group-header-row {
            background: #f1f5f9;
            page-break-after: avoid;
        }

        .group-header-cell {
            padding: 8px 10px;
            font-size: 10pt;
            color: #1e3a8a;
            border-bottom: 2px solid #3b82f6;
            background: linear-gradient(135deg, #f8fafc, #f1f5f9);
        }

        .group-total {
            margin-left: 8px;
            font-size: 0.85em;
            color: #059669;
            font-weight: 600;
        }

        .group-description {
            margin-top: 3px;
            font-size: 0.85em;
            color: #64748b;
            font-weight: normal;
        }

        .criterion-header {
            background: #f1f5f9;
            font-weight: 600;
            color: #334155;
            font-size: 8.5pt;
        }

        .criterion-row {
            page-break-inside: avoid;
        }

        /* Dynamic level-specific CSS */
        ${dynamicLevelStyles}

        .points {
            font-weight: 600;
            color: #059669;
            text-align: center;
        }

        .achievement-levels-section {
            margin-bottom: 15px;
            page-break-before: auto;
            page-break-inside: avoid;
        }

        .achievement-levels-section h2 {
            color: #1e40af;
            margin-bottom: 10px;
            font-size: 12pt;
            page-break-after: avoid;
        }

        .achievement-levels-section p {
            color: #64748b;
            margin-bottom: 10px;
            font-size: 8.5pt;
        }

        .achievement-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
            gap: 8px;
        }

        .achievement-grid > div {
            padding: 10px;
            border-radius: 6px;
            text-align: center;
            font-weight: 600;
            font-size: 8pt;
            page-break-inside: avoid;
        }

        .footer {
            text-align: center;
            color: #666;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px solid #e5e7eb;
            font-size: 7pt;
            page-break-inside: avoid;
        }

        /* Print-specific rules */
        @media print {
            body {
                background: white;
            }

            .table-section {
                page-break-inside: avoid;
            }

            .rubric-table thead {
                display: table-header-group;
            }

            .criterion-row {
                page-break-inside: avoid;
            }

            .group-header-row {
                page-break-after: avoid;
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="header-content">
            <h1>${reportData.title}</h1>
            <div class="meta">
                Generated: ${generatedAt.toLocaleDateString()} | ${generatedAt.toLocaleTimeString()}
            </div>
        </div>
        ${getSchoolLogo() ? `<img src="${getSchoolLogo()}" alt="School Logo" class="report-logo" />` : ''}
    </div>

    <div class="assignment-info">
        <h2>Assignment Information</h2>
        <div class="info-grid">
            <div><strong>Weight:</strong> ${rubricData.assignmentInfo.weight || 0}%</div>
            <div><strong>Total Points:</strong> ${totalPointsValue}</div>
            <div><strong>Passing Threshold:</strong> ${rubricData.assignmentInfo.passingThreshold || 0}%</div>
        </div>
        ${rubricData.assignmentInfo.description ? `<div class="description"><strong>Description:</strong> ${rubricData.assignmentInfo.description}</div>` : ''}
    </div>

    ${groupTables}${ungroupedTable}

    <div class="achievement-levels-section">
        <h2>Overall Achievement Levels</h2>
        <p>Students receive an overall achievement level based on their total points across all criteria.</p>
        <div class="achievement-grid">
            ${overallLevels
                .map(
                    (level) => `
            <div class="achievement-${level.level}">
                <div style="font-size: 1em; margin-bottom: 4px;">${level.name}</div>
                <div style="font-size: 1.4em; font-weight: bold; margin-bottom: 3px;">${Math.round((level.multiplier || 0) * totalPointsValue)}+ pts</div>
                <div style="font-size: 0.85em; opacity: 0.8;">${((level.multiplier || 0) * 100).toFixed(0)}%+</div>
            </div>
            `
                )
                .join('')}
        </div>
    </div>

    <div class="footer">
        <p><strong>Assessment Rubric</strong> | Generated on ${generatedAt.toLocaleDateString()} at ${generatedAt.toLocaleTimeString()}</p>
    </div>
</body>
</html>`;
        } catch (error) {
            console.error('Error building PDF content:', error);
            throw new Error(`Failed to build PDF content: ${error.message}`);
        }
    };

    const downloadBlob = (content, filename, type) => {
        const blob = new Blob([content], { type });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportRubricHTML = () => {
        try {
            const htmlContent = buildRubricHTMLContent();
            if (!htmlContent || htmlContent.length === 0) {
                console.error('Failed to generate HTML content');
                alert('Failed to generate HTML content. Please check the console for errors.');
                return;
            }
            const filename = rubricData.assignmentInfo.title
                ? rubricData.assignmentInfo.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()
                : 'untitled';
            downloadBlob(htmlContent, `rubric_${filename}.html`, 'text/html');
        } catch (error) {
            console.error('Error exporting HTML:', error);
            alert(`Failed to export HTML: ${error.message}`);
        }
    };

    const exportRubricPDF = () => {
        try {
            const pdfContent = buildRubricPDFContent();
            if (!pdfContent || pdfContent.length === 0) {
                console.error('Failed to generate PDF content');
                alert('Failed to generate PDF content. Please check the console for errors.');
                return;
            }

            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                alert('Failed to open print window. Please check if pop-ups are blocked.');
                return;
            }

            printWindow.document.open();
            printWindow.document.write(pdfContent);
            printWindow.document.close();

            const triggerPrint = () => {
                printWindow.focus();
                setTimeout(() => {
                    printWindow.print();
                }, 500);
            };

            if (printWindow.document.readyState === 'complete') {
                triggerPrint();
            } else {
                printWindow.onload = triggerPrint;
                // Fallback if onload doesn't fire
                setTimeout(triggerPrint, 1000);
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            alert(`Failed to export PDF: ${error.message}`);
        }
    };

    const resetPage = () => {
        const fresh = createDefaultRubric();
        setRubricData(fresh);
        setSharedRubric(fresh);
        setExpandedSections({ assignment: true, levels: false, criteria: true });
        selectCriterion(fresh.criteria[0].id);
        setPasteAccordionOpen(false);
        setPasteJSONText('');

        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const updateAssignmentInfo = useCallback((field, value) => {
        setRubricData((prev) => ({ ...prev, assignmentInfo: { ...prev.assignmentInfo, [field]: value } }));
    }, []);

    const addCriterion = useCallback(
        (groupId = null) => {
            const newId = `criterion-${Date.now()}`;
            setRubricData((prev) => {
                const levelsForNewCriterion = groupId ? getGroupLevels(groupId) : prev.rubricLevels;
                const newCriterion = { ...createEmptyCriterion(newId, levelsForNewCriterion), groupId };
                const updated = { ...prev, criteria: [...prev.criteria, newCriterion] };
                setSharedRubric(updated);
                return updated;
            });
            selectCriterion(newId);
            if (groupId) {
                setExpandedGroups((prev) => ({ ...prev, [groupId]: true }));
                setActiveGroup(groupId);
            }
        },
        [getGroupLevels, selectCriterion]
    );

    const addCriterionToActiveGroup = useCallback(() => addCriterion(activeGroup), [activeGroup, addCriterion]);

    const removeCriterion = useCallback(
        (criterionId) => {
            setRubricData((prev) => {
                const filtered = prev.criteria.filter((criterion) => criterion.id !== criterionId);
                const updated = { ...prev, criteria: filtered };

                if (criterionId === activeCriterion) {
                    const removedIndex = prev.criteria.findIndex((criterion) => criterion.id === criterionId);
                    const fallback = filtered[removedIndex] || filtered[removedIndex - 1] || filtered[0];
                    if (fallback) {
                        selectCriterion(fallback.id);
                    } else {
                        setActiveCriterion(null);
                    }
                }

                return updated;
            });
        },
        [activeCriterion, selectCriterion]
    );

    const updateCriterion = useCallback(
        (criterionId, field, value) => {
            setRubricData((prev) => ({
                ...prev,
                criteria: prev.criteria.map((criterion) => {
                    if (criterion.id !== criterionId) return criterion;

                    if (field === 'groupId') {
                        const levelsForGroup = getGroupLevels(value || null);
                        const nextLevelMap = levelsForGroup.reduce((acc, level) => {
                            acc[level.level] = criterion.levels?.[level.level] || { description: '' };
                            return acc;
                        }, {});
                        return { ...criterion, groupId: value, levels: nextLevelMap };
                    }

                    return { ...criterion, [field]: value };
                })
            }));
        },
        [getGroupLevels]
    );

    const updateCriterionLevel = useCallback((criterionId, level, field, value) => {
        setRubricData((prev) => ({
            ...prev,
            criteria: prev.criteria.map((criterion) => {
                if (criterion.id !== criterionId) return criterion;
                const existingLevel = criterion.levels[level] || {};
                return {
                    ...criterion,
                    levels: {
                        ...criterion.levels,
                        [level]: { ...existingLevel, [field]: value }
                    }
                };
            })
        }));
    }, []);

    const defaultOverallDescriptions = {
        incomplete: 'FAILING. No assessable submission is present. Required components are missing, unusable, or not provided in a format that can be evaluated. The assignment criteria cannot be meaningfully assessed at this time.',
        unacceptable: 'FAILING. The submission does not meet the minimum expectations for the assignment. Significant deficiencies are present across multiple areas such as completeness, clarity, execution, or required process. Substantial revision is required.',
        developing: 'PASSING (Minimum Threshold). The submission meets the minimum requirements to pass. Core components are present and the overall intent is understandable, but execution is inconsistent or underdeveloped. Targeted refinement is needed to improve clarity and consistency.',
        acceptable: 'PASSING. The submission meets assignment expectations in a clear and competent manner. Required components are complete and generally consistent. Minor issues may be present but do not prevent understanding or progress.',
        emerging: 'PASSING. A strong and cohesive submission demonstrating growing control and confidence. Components work well together and intent is clearly communicated. Only minor refinements are needed.',
        accomplished: 'PASSING. A highly polished submission demonstrating strong proficiency and attention to detail. The work is cohesive, consistent, and well executed, approaching professional standards.',
        exceptional: 'PASSING. An outstanding submission demonstrating exceptional clarity, consistency, and control. All components are seamlessly integrated, and decisions are confident and purposeful. The work exceeds expectations.'
    };

    const applyDefaultOverallDescriptions = useCallback(() => {
        const hasExistingText = rubricData.overallAchievementLevels.some(
            lvl => lvl.description && lvl.description.trim() !== ''
        );

        if (hasExistingText) {
            const confirmed = window.confirm(
                'Some Overall Achievement levels already have descriptions.\n\nDo you want to replace them with the default descriptions?'
            );
            if (!confirmed) return;
        }

        setRubricData(prev => ({
            ...prev,
            overallAchievementLevels: prev.overallAchievementLevels.map(lvl => {
                const defaultDesc = defaultOverallDescriptions[lvl.level] || '';
                if (!defaultDesc) return lvl;
                return {
                    ...lvl,
                    description: defaultDesc,
                    overallComment: defaultDesc
                };
            })
        }));
    }, [rubricData.overallAchievementLevels]);

    const updateOverallLevelDescription = useCallback((levelValue, description) => {
        setRubricData((prev) => ({
            ...prev,
            overallAchievementLevels: prev.overallAchievementLevels.map((lvl) =>
                lvl.level === levelValue ? { ...lvl, description, overallComment: description } : lvl
            )
        }));
    }, []);

    const updateLevelName = useCallback((levelValue, name) => {
        setRubricData((prev) => ({
            ...prev,
            rubricLevels: prev.rubricLevels.map((lvl) =>
                lvl.level === levelValue ? { ...lvl, name } : lvl
            ),
            overallAchievementLevels: prev.overallAchievementLevels.map((lvl) =>
                lvl.level === levelValue ? { ...lvl, name } : lvl
            )
        }));
    }, []);

    const changeLevelCount = useCallback((count) => {
        setRubricData((prev) => ({ ...prev, rubricLevels: resizeLevels(prev.rubricLevels, count) }));
    }, []);

    const changeOverallLevelCount = useCallback((count) => {
        setRubricData((prev) => ({ ...prev, overallAchievementLevels: resizeLevels(prev.overallAchievementLevels, count) }));
    }, []);

    const getLevelPoints = useCallback(
        (criterion, levelValue, rubricLevels) => {
            const levelsForCalc = rubricLevels || getGroupLevels(criterion?.groupId);
            return calculateLevelPoints({
                criterion,
                levelKey: levelValue,
                rubricLevels: levelsForCalc
            });
        },
        [getGroupLevels]
    );

    const setCriterionPointMode = useCallback((criterionId, useAutoPointValues) => {
        setRubricData((prev) => ({
            ...prev,
            criteria: prev.criteria.map((criterion) => {
                if (criterion.id !== criterionId) return criterion;

                if (useAutoPointValues) {
                    const cleanedLevels = Object.entries(criterion.levels || {}).reduce((acc, [levelKey, levelData]) => {
                        const { pointRange, ...rest } = levelData || {};
                        acc[levelKey] = { ...rest };
                        return acc;
                    }, {});

                    return {
                        ...criterion,
                        useAutoPointValues,
                        levels: cleanedLevels
                    };
                }

                const seededLevels = { ...(criterion.levels || {}) };
                const criterionLevels = getGroupLevels(criterion.groupId);
                criterionLevels.forEach((level) => {
                    const existing = seededLevels[level.level] || {};
                    if (existing.pointRange === undefined || existing.pointRange === null || existing.pointRange === '') {
                        const derived = getLevelPoints(criterion, level.level, criterionLevels);
                        seededLevels[level.level] = { ...existing, pointRange: derived };
                    } else {
                        seededLevels[level.level] = existing;
                    }
                });

                return {
                    ...criterion,
                    useAutoPointValues,
                    levels: seededLevels
                };
            })
        }));
    }, [getGroupLevels, getLevelPoints]);

    const updateCriterionLevelPoints = useCallback((criterionId, level, value) => {
        setRubricData((prev) => ({
            ...prev,
            criteria: prev.criteria.map((criterion) => {
                if (criterion.id !== criterionId) return criterion;
                const existingLevel = criterion.levels[level] || {};
                return {
                    ...criterion,
                    levels: {
                        ...criterion.levels,
                        [level]: {
                            ...existingLevel,
                            pointRange: value
                        }
                    }
                };
            })
        }));
    }, []);

    const handleLevelPointsChange = useCallback(
        (levelKey, rawValue) => {
            const parsed = rawValue === '' ? '' : parseFloat(rawValue);
            updateCriterionLevelPoints(activeCriterion, levelKey, parsed);
        },
        [activeCriterion, updateCriterionLevelPoints]
    );

    const handleLevelDescriptionChange = useCallback(
        (levelKey, value) => {
            updateCriterionLevel(activeCriterion, levelKey, 'description', value);
        },
        [activeCriterion, updateCriterionLevel]
    );

    const handleWeightKeyDown = useCallback(
        (e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();

            const parsedWeight = parseFloat(e.target.value);
            if (Number.isNaN(parsedWeight)) {
                return;
            }

            const assignmentTotalPoints = rubricData.assignmentInfo.totalPoints || 100;
            const weightPercent = Math.max(0, parsedWeight);
            const newMaxPoints = Math.round((weightPercent / 100) * assignmentTotalPoints);
            updateCriterion(activeCriterion, 'weight', weightPercent);
            updateCriterion(activeCriterion, 'maxPoints', newMaxPoints);
        },
        [activeCriterion, rubricData.assignmentInfo.totalPoints, updateCriterion]
    );

    const downloadJSONFile = (data, filename) => {
        const dataStr = JSON.stringify(data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    };

    const formatDateStamp = () => {
        const now = new Date();
        const month = now.toLocaleString('en-US', { month: 'short' });
        const day = now.getDate();
        const year = now.getFullYear();
        return `${month} ${day} ${year}`;
    };

    const buildExportableRubric = () => {
        const normalized = normalizeRubricData(rubricData);
        return {
            ...normalized,
            rubricLevels: normalized.rubricLevels.map((lvl) => ({ ...lvl })),
            overallAchievementLevels: (normalized.overallAchievementLevels || normalized.rubricLevels || []).map((lvl) => ({ ...lvl }))
        };
    };

    const buildRubricFilename = () => {
        const title = rubricData.assignmentInfo.title?.trim() || 'Rubric';
        return `${title} - ${formatDateStamp()}.json`;
    };

    const exportRubricJSON = () => {
        const exportable = buildExportableRubric();
        downloadJSONFile(exportable, buildRubricFilename());
    };

    const saveDraftAsFile = () => {
        const filename = rubricData.assignmentInfo.title
            ? `${rubricData.assignmentInfo.title.replace(/\s+/g, '_').toLowerCase()}_draft.json`
            : 'rubric_draft.json';

        const exportable = buildExportableRubric();
        setSharedRubric(exportable);
        downloadJSONFile(exportable, filename);
    };

    const exportRubricTemplate = () => {
        const levelEntries = defaultLevels.map((level) => ({
            ...level,
            name: level.name,
            description: '',
            overallComment: ''
        }));

        const baseLevelsMap = defaultLevels.reduce((acc, lvl) => {
            acc[lvl.level] = { description: '' };
            return acc;
        }, {});

        const template = {
            assignmentInfo: {
                title: '',
                description: '',
                weight: 0,
                passingThreshold: 0,
                totalPoints: 0
            },
            rubricLevels: levelEntries,
            overallAchievementLevels: levelEntries,
            criteria: [
                {
                    id: 'criterion-1',
                    name: '',
                    description: '',
                    maxPoints: 0,
                    weight: 0,
                    levels: { ...baseLevelsMap },
                    feedbackLibrary: {
                        strengths: [],
                        improvements: [],
                        general: []
                    }
                }
            ]
        };

        downloadJSONFile(template, 'rubric_template.json');
    };

    const exportRubricFinalJSON = () => {
        const exportable = buildExportableRubric();
        downloadJSONFile(exportable, buildRubricFilename());
    };

    const exportCriterionJSON = useCallback((criterionId) => {
        const criterion = rubricData.criteria.find(c => c.id === criterionId);
        if (!criterion) return;

        const criterionIndex = rubricData.criteria.indexOf(criterion);
        const criterionLevels = getGroupLevels(criterion.groupId);

        const payload = {
            schema: 'gradingpilot-criterion',
            schema_version: '1.0',
            export_type: 'criterion',
            created_at: new Date().toISOString(),
            producer: 'GradingPilot Professional Suite v2.0',
            rubric_context: {
                level_count: criterionLevels.length,
                grading_scale_levels: criterionLevels.map(l => l.level)
            },
            criterion: {
                name: criterion.name || '',
                description: criterion.description || '',
                maxPoints: criterion.maxPoints || 0,
                weight: criterion.weight || 0,
                order: criterionIndex,
                useAutoPointValues: criterion.useAutoPointValues !== false,
                levels: criterionLevels.reduce((acc, level) => {
                    const levelData = criterion.levels?.[level.level] || {};
                    acc[level.level] = {
                        label: level.name,
                        pointValue: getLevelPoints(criterion, level.level, criterionLevels),
                        description: levelData.description || '',
                        ...(levelData.pointRange !== undefined && levelData.pointRange !== '' ? { customPointRange: levelData.pointRange } : {})
                    };
                    return acc;
                }, {}),
                feedbackLibrary: {
                    strengths: Array.isArray(criterion.feedbackLibrary?.strengths) ? criterion.feedbackLibrary.strengths : [],
                    improvements: Array.isArray(criterion.feedbackLibrary?.improvements) ? criterion.feedbackLibrary.improvements : [],
                    general: Array.isArray(criterion.feedbackLibrary?.general) ? criterion.feedbackLibrary.general : []
                }
            }
        };

        const safeName = (criterion.name || 'Untitled').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '');
        const dateStamp = new Date().toISOString().split('T')[0];
        const filename = `Criterion_${safeName}_${dateStamp}.json`;

        downloadJSONFile(payload, filename);
    }, [rubricData.criteria, getGroupLevels, getLevelPoints, downloadJSONFile]);

    const criterionImportInputRef = useRef(null);

    const handleImportCriterionFile = useCallback((event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);

                // Validate schema
                if (data.schema !== 'gradingpilot-criterion' || data.export_type !== 'criterion') {
                    alert('Invalid file: This does not appear to be a GradingPilot criterion export.\n\nExpected a file with schema "gradingpilot-criterion" and export_type "criterion".');
                    return;
                }

                const imported = data.criterion;
                if (!imported) {
                    alert('Invalid file: No criterion data found.');
                    return;
                }

                // Check level count compatibility
                const importedLevelKeys = Object.keys(imported.levels || {});
                const currentLevelKeys = rubricData.rubricLevels.map(l => l.level);

                if (importedLevelKeys.length !== currentLevelKeys.length) {
                    alert(
                        `Level count mismatch: The imported criterion has ${importedLevelKeys.length} achievement levels, ` +
                        `but the current rubric uses ${currentLevelKeys.length} levels.\n\n` +
                        `Imported levels: ${importedLevelKeys.join(', ')}\n` +
                        `Current rubric levels: ${currentLevelKeys.join(', ')}\n\n` +
                        `Please adjust the rubric's level count to match before importing, or export a criterion from a rubric with the same number of levels.`
                    );
                    return;
                }

                // Build the new criterion
                const newId = `criterion-${Date.now()}`;
                const levelsObj = {};
                currentLevelKeys.forEach(levelKey => {
                    const importedLevel = imported.levels?.[levelKey] || {};
                    levelsObj[levelKey] = {
                        description: importedLevel.description || '',
                        ...(importedLevel.customPointRange !== undefined ? { pointRange: importedLevel.customPointRange } : {})
                    };
                });

                const newCriterion = {
                    id: newId,
                    groupId: null,
                    name: imported.name || '',
                    description: imported.description || '',
                    maxPoints: Number(imported.maxPoints) || 0,
                    weight: Number(imported.weight) || 0,
                    useAutoPointValues: imported.useAutoPointValues !== false,
                    levels: levelsObj,
                    feedbackLibrary: {
                        strengths: Array.isArray(imported.feedbackLibrary?.strengths) ? imported.feedbackLibrary.strengths : [],
                        improvements: Array.isArray(imported.feedbackLibrary?.improvements) ? imported.feedbackLibrary.improvements : [],
                        general: Array.isArray(imported.feedbackLibrary?.general) ? imported.feedbackLibrary.general : []
                    }
                };

                setRubricData(prev => ({
                    ...prev,
                    criteria: [...prev.criteria, newCriterion]
                }));
                selectCriterion(newId);
                alert(`Criterion "${imported.name || 'Untitled'}" imported successfully.`);
            } catch (err) {
                console.error('Failed to import criterion JSON', err);
                alert('Error importing criterion: Invalid JSON file.');
            }
        };
        reader.readAsText(file);

        if (event.target) {
            event.target.value = '';
        }
    }, [rubricData.rubricLevels, selectCriterion]);

    const exportOverallAchievementJSON = () => {
        const title = rubricData.assignmentInfo.title?.trim() || 'Rubric';
        const overallLevels = rubricData.overallAchievementLevels?.length
            ? rubricData.overallAchievementLevels
            : rubricData.rubricLevels || [];
        const formattedLevels = overallLevels.map((level) => ({
            level: level.level,
            name: level.name || '',
            description: level.description ?? level.overallComment ?? '',
            color: level.color || '',
            multiplier: Number.isFinite(level.multiplier) ? level.multiplier : null,
            overallComment: level.overallComment ?? level.description ?? ''
        }));

        const payload = {
            format: 'overall-achievement-levels',
            formatVersion: 1,
            rubricTitle: title,
            generatedAt: new Date().toISOString(),
            instructions:
                'Edit the description field for each level to update the Overall Feedback text shown in the Professional Rubric Builder.',
            overallLevelCount: formattedLevels.length,
            overallAchievementLevels: formattedLevels
        };

        downloadJSONFile(payload, `${title} - Overall Achievement Levels - ${formatDateStamp()}.json`);
    };

    const loadRubricData = (data, { resetPaste = false } = {}) => {
        const normalized = normalizeRubricData(data);
        setRubricData(normalized);
        setSharedRubric(normalized);
        selectCriterion(normalized.criteria?.[0]?.id || 'criterion-1');
        if (resetPaste) {
            setPasteAccordionOpen(false);
            setPasteJSONText('');
        }
    };

    const handleImportFile = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                loadRubricData(data, { resetPaste: true });
            } catch (err) {
                console.error('Failed to import rubric JSON', err);
            }
        };
        reader.readAsText(file);
    };

    const handlePasteJSON = () => {
        setPasteAccordionOpen((prev) => !prev);
    };

    const applyPastedJSON = () => {
        if (!pasteJSONText.trim()) return;
        try {
            const data = JSON.parse(pasteJSONText);
            loadRubricData(data, { resetPaste: true });
        } catch (err) {
            console.error('Invalid JSON', err);
        }
    };

    const useForGrading = () => {
        const readyRubric = normalizeRubricData(rubricData);
        setSharedRubric(readyRubric);
        transferRubricToGradingWithDetails?.(readyRubric);
        initializeGradingSession?.(classList || []);
        setActiveTab('grading-tool');
    };

    const handleAddFeedbackItem = (type, text) => {
        setRubricData((prev) => ({
            ...prev,
            criteria: prev.criteria.map((criterion) =>
                criterion.id === activeCriterion
                    ? {
                        ...criterion,
                        feedbackLibrary: {
                            ...criterion.feedbackLibrary,
                            [type]: [...(criterion.feedbackLibrary?.[type] || []), text]
                        }
                    }
                    : criterion
            )
        }));
    };

    const handleRemoveFeedbackItem = (type, index) => {
        setRubricData((prev) => ({
            ...prev,
            criteria: prev.criteria.map((criterion) => {
                if (criterion.id !== activeCriterion) return criterion;
                const updated = [...(criterion.feedbackLibrary?.[type] || [])];
                updated.splice(index, 1);
                return {
                    ...criterion,
                    feedbackLibrary: {
                        ...criterion.feedbackLibrary,
                        [type]: updated
                    }
                };
            })
        }));
    };

    const handleUpdateFeedbackItem = (type, index, text) => {
        setRubricData((prev) => ({
            ...prev,
            criteria: prev.criteria.map((criterion) => {
                if (criterion.id !== activeCriterion) return criterion;
                const existing = [...(criterion.feedbackLibrary?.[type] || [])];
                if (index < 0 || index >= existing.length) return criterion;
                existing[index] = text;
                return {
                    ...criterion,
                    feedbackLibrary: {
                        ...criterion.feedbackLibrary,
                        [type]: existing
                    }
                };
            })
        }));
    };

    const normalizeFeedbackLibrary = (incoming = {}) => ({
        strengths: Array.isArray(incoming.strengths) ? incoming.strengths : [],
        improvements: Array.isArray(incoming.improvements) ? incoming.improvements : [],
        general: Array.isArray(incoming.general) ? incoming.general : []
    });

    const handleExportFeedbackLibrary = () => {
        const title = rubricData.assignmentInfo.title?.trim() || 'Rubric';
        const filename = `${title} - Feedback Comments - ${formatDateStamp()}.json`;

        const payload = {
            rubricTitle: title,
            generatedAt: new Date().toISOString(),
            criteria: rubricData.criteria.map((criterion) => ({
                id: criterion.id,
                name: criterion.name || '',
                feedbackLibrary: normalizeFeedbackLibrary(criterion.feedbackLibrary)
            }))
        };

        downloadJSONFile(payload, filename);
    };

    const handleImportOverallAchievementFile = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                const incomingLevels = Array.isArray(data)
                    ? data
                    : Array.isArray(data.overallAchievementLevels)
                        ? data.overallAchievementLevels
                        : Array.isArray(data.rubricLevels)
                            ? data.rubricLevels
                            : [];
                const fallbackLevels = rubricData.overallAchievementLevels?.length
                    ? rubricData.overallAchievementLevels
                    : rubricData.rubricLevels;
                const normalizedLevels = normalizeOverallAchievementLevels(
                    incomingLevels,
                    fallbackLevels?.length ? fallbackLevels : defaultLevels
                );

                if (!normalizedLevels.length) return;

                setRubricData((prev) => ({
                    ...prev,
                    overallAchievementLevels: normalizedLevels
                }));
            } catch (err) {
                console.error('Failed to import overall achievement JSON', err);
            }
        };

        reader.readAsText(file);

        if (event.target) {
            event.target.value = '';
        }
    };

    const handleImportFeedbackFile = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);

                // Support full rubric feedback exports (preferred)
                if (Array.isArray(data.criteria)) {
                    setRubricData((prev) => ({
                        ...prev,
                        criteria: prev.criteria.map((criterion) => {
                            const matchById = data.criteria.find((item) => item.id === criterion.id);
                            const matchByName = data.criteria.find(
                                (item) => !matchById && item.name && item.name === criterion.name
                            );
                            const matched = matchById || matchByName;

                            if (!matched) return criterion;

                            return {
                                ...criterion,
                                feedbackLibrary: normalizeFeedbackLibrary(matched.feedbackLibrary || matched)
                            };
                        })
                    }));
                } else {
                    // Fallback: import to active criterion only
                    const incoming = data.feedbackLibrary || data;
                    const normalized = normalizeFeedbackLibrary(incoming);

                    setRubricData((prev) => ({
                        ...prev,
                        criteria: prev.criteria.map((criterion) =>
                            criterion.id === activeCriterion
                                ? { ...criterion, feedbackLibrary: normalized }
                                : criterion
                        )
                    }));
                }
            } catch (err) {
                console.error('Failed to import feedback JSON', err);
            }
        };

        reader.readAsText(file);
        event.target.value = '';
    };

    const levelCount = rubricData.rubricLevels.length;
    const overallLevelCount = rubricData.overallAchievementLevels?.length || levelCount;
    const overallLevels = rubricData.overallAchievementLevels?.length
        ? rubricData.overallAchievementLevels
        : rubricData.rubricLevels;
    const activeCriterionData = rubricData.criteria.find((criterion) => criterion.id === activeCriterion);
    const activeLevels = useMemo(
        () => getGroupLevels(activeCriterionData?.groupId),
        [getGroupLevels, activeCriterionData?.groupId]
    );

    if (!activeCriterionData) {
        return (
            <div className="p-6">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">No criterion selected.</div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen">
            <style>{`
                .card {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
                    overflow: hidden;
                }

                .card-header {
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background: #f9fafb;
                }

                .card-header.teal {
                    background: linear-gradient(135deg, #e0f2fe, #ccfbf1);
                }

                .card-content {
                    padding: 12px;
                }

                .groups-container {
                    max-height: 500px;
                    overflow-y: auto;
                    padding: 0;
                }

                .group-wrapper {
                    margin-bottom: 12px;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    background: white;
                    transition: all 0.2s;
                }

                .group-wrapper.has-active {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .group-wrapper.collapsed {
                    border-color: #d1d5db;
                }

                .group-header-bar {
                    padding: 10px 12px;
                    background: #f9fafb;
                    border-bottom: 1px solid #e5e7eb;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    transition: background 0.2s;
                    user-select: none;
                }

                .group-wrapper.has-active .group-header-bar {
                    background: linear-gradient(135deg, #eff6ff, #dbeafe);
                }

                .group-header-bar:hover {
                    background: #f3f4f6;
                }

                .group-wrapper.has-active .group-header-bar:hover {
                    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
                }

                .group-info {
                    flex: 1;
                    min-width: 0;
                }

                .group-name {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1f2937;
                    margin-bottom: 2px;
                }

                .group-stats {
                    font-size: 10px;
                    color: #6b7280;
                }

                .collapse-icon {
                    color: #6b7280;
                    font-size: 12px;
                    transition: transform 0.2s;
                }

                .group-wrapper.collapsed .collapse-icon {
                    transform: rotate(-90deg);
                }

                .group-actions {
                    display: flex;
                    gap: 4px;
                    opacity: 0;
                    visibility: hidden;
                    pointer-events: none;
                    transition: opacity 0.2s;
                }

                .group-wrapper:hover .group-actions {
                    opacity: 1;
                    visibility: visible;
                    pointer-events: auto;
                }

                .group-criteria {
                    border-top: 1px solid #f3f4f6;
                    background: #fafafa;
                }

                .criterion-item {
                    padding: 10px 12px;
                    border-bottom: 1px solid #f0f0f0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    background: white;
                    position: relative;
                }

                .criterion-item:last-child { border-bottom: none; }

                .criterion-item:hover {
                    background: #f0f9ff;
                    transform: translateX(2px);
                }

                .criterion-item.active {
                    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
                    border-left: 4px solid #3b82f6;
                    padding-left: 8px;
                    box-shadow: inset 0 0 0 1px #93c5fd;
                }

                .criterion-item.active::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    width: 4px;
                    background: linear-gradient(180deg, #3b82f6, #2563eb);
                    border-radius: 0 4px 4px 0;
                }

                .criterion-drag {
                    color: #d1d5db;
                    cursor: grab;
                    font-size: 14px;
                    transition: color 0.2s;
                }

                .criterion-item:hover .criterion-drag { color: #9ca3af; }
                .criterion-item.active .criterion-drag { color: #60a5fa; }

                .criterion-number {
                    font-size: 11px;
                    color: #9ca3af;
                    font-weight: 700;
                    min-width: 24px;
                    text-align: center;
                    background: #f3f4f6;
                    padding: 4px 6px;
                    border-radius: 4px;
                    transition: all 0.2s;
                }

                .criterion-item:hover .criterion-number { background: #e0f2fe; color: #0284c7; }
                .criterion-item.active .criterion-number { background: #3b82f6; color: white; }

                .criterion-name {
                    flex: 1;
                    font-size: 13px;
                    color: #374151;
                    font-weight: 600;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    transition: color 0.2s;
                }

                .criterion-item:hover .criterion-name { color: #1f2937; }
                .criterion-item.active .criterion-name { color: #1e40af; font-weight: 700; }

                .criterion-points {
                    font-size: 11px;
                    color: #059669;
                    font-weight: 700;
                    background: #d1fae5;
                    padding: 4px 8px;
                    border-radius: 4px;
                    transition: all 0.2s;
                }

                .criterion-delete-btn {
                    opacity: 0;
                    border: none;
                    background: transparent;
                    color: #ef4444;
                    padding: 4px;
                    border-radius: 6px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }

                .criterion-delete-btn:hover {
                    background: #fee2e2;
                    color: #b91c1c;
                }

                .criterion-item:hover .criterion-delete-btn,
                .criterion-item.active .criterion-delete-btn {
                    opacity: 1;
                }

                .criterion-item:hover .criterion-points { background: #a7f3d0; }
                .criterion-item.active .criterion-points { background: #10b981; color: white; }

                .criterion-nav {
                    opacity: 0;
                    transition: opacity 0.2s;
                    color: #9ca3af;
                    font-size: 12px;
                }

                .criterion-item:hover .criterion-nav { opacity: 1; }
                .criterion-item.active .criterion-nav { opacity: 1; color: #3b82f6; }

                .ungrouped-section {
                    margin-top: 12px;
                    padding-top: 12px;
                    border-top: 2px dashed #e5e7eb;
                }

                .ungrouped-label {
                    font-size: 11px;
                    color: #6b7280;
                    font-weight: 600;
                    text-transform: uppercase;
                    padding: 0 12px 8px 12px;
                    letter-spacing: 0.5px;
                }

                .group-selector-wrapper {
                    background: #fef3c7;
                    border: 1px solid #fbbf24;
                    border-radius: 8px;
                    padding: 12px;
                    margin-bottom: 20px;
                }

                .group-selector-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    font-weight: 700;
                    color: #92400e;
                    margin-bottom: 8px;
                }

                .group-select { background: white; font-weight: 600; width: 100%; padding: 8px; border-radius: 8px; border: 1px solid #d1d5db; }

                .help-text {
                    margin-top: 8px;
                    font-size: 11px;
                    color: #92400e;
                    display: flex;
                    align-items: start;
                    gap: 6px;
                }

                .criterion-nav-buttons { display: flex; gap: 8px; align-items: center; }

                .nav-btn {
                    background: white;
                    border: 1px solid #d1d5db;
                    padding: 6px 12px;
                    border-radius: 6px;
                    font-size: 12px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    transition: all 0.2s;
                    color: #374151;
                }

                .nav-btn:hover:not(:disabled) {
                    background: #f9fafb;
                    border-color: #3b82f6;
                    color: #1e40af;
                }

                .nav-btn:disabled { opacity: 0.4; cursor: not-allowed; }

                .criterion-counter { font-size: 12px; color: #6b7280; font-weight: 600; padding: 0 8px; }

                .add-btn-full {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px dashed #d1d5db;
                    background: #fafafa;
                    color: #374151;
                    border-radius: 6px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    transition: all 0.2s;
                    margin-top: 8px;
                }

                .add-btn-full:hover { background: #f3f4f6; border-color: #9ca3af; }
                .add-btn-full.primary { background: #eff6ff; border-color: #3b82f6; color: #1e40af; }
                .add-btn-full.primary:hover { background: #dbeafe; }
                .add-btn-full.success { background: #f0fdf4; border-color: #10b981; color: #047857; }
                .add-btn-full.success:hover { background: #dcfce7; }

                .icon-btn-sm {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    padding: 4px 6px;
                    cursor: pointer;
                    font-size: 12px;
                }

                .icon-btn-sm.danger { color: #dc2626; border-color: #fecdd3; }

                .form-section { margin-bottom: 12px; }
                .form-label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 6px; }
                .form-input { width: 100%; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; font-size: 13px; }
                .toggle-section { display: flex; flex-direction: column; gap: 8px; margin: 12px 0; }
                .toggle-option { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #374151; }
                .group-statistics { border-top: 1px solid #e5e7eb; padding-top: 10px; display: grid; gap: 6px; }
                .stat-row { display: flex; justify-content: space-between; font-size: 13px; color: #374151; }
            `}</style>
            <input type="file" accept="application/json" className="hidden" ref={fileInputRef} onChange={handleImportFile} />
            <input type="file" accept="application/json" className="hidden" ref={criterionImportInputRef} onChange={handleImportCriterionFile} />
            <input
                type="file"
                accept="application/json"
                className="hidden"
                ref={overallAchievementFileInputRef}
                onChange={handleImportOverallAchievementFile}
            />
            <input
                type="file"
                accept="application/json"
                className="hidden"
                ref={feedbackFileInputRef}
                onChange={handleImportFeedbackFile}
            />
            <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-full px-6 py-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                                <FileText className="text-white" size={24} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-800">Professional Rubric Builder</h1>
                                <p className="text-sm text-gray-600">Create comprehensive assessment rubrics with detailed criteria and feedback</p>
                            </div>
                        </div>
                        <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg" onClick={resetPage}>
                            🔄 Reset Page to Default
                        </button>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center gap-3 flex-wrap">
                            <button
                                className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-100 via-emerald-200 to-emerald-300 hover:from-emerald-200 hover:to-emerald-400 text-emerald-900 rounded-lg flex items-center justify-center gap-3 transition-all shadow-md font-semibold text-base border border-emerald-300"
                                onClick={useForGrading}
                            >
                                <ArrowRight size={20} />
                                <span>Use for Grading</span>
                                {classList?.length > 0 && (
                                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm">{classList.length} students</span>
                                )}
                            </button>
                            <button
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-all shadow-md font-semibold"
                                onClick={exportRubricJSON}
                            >
                                <Download size={20} />
                                <span>Export JSON</span>
                            </button>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-2 mr-4">
                                <span className="text-xs font-semibold text-gray-600 uppercase">File:</span>
                                <button
                                    className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-md flex items-center gap-1.5 transition-all text-sm font-medium"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Upload size={14} />
                                    <span>Import</span>
                                </button>
                                <button
                                    className="px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-md flex items-center gap-1.5 transition-all text-sm font-medium"
                                    onClick={handlePasteJSON}
                                >
                                    📋 <span>Paste JSON</span>
                                </button>
                                <button
                                    className="px-3 py-1.5 bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-md flex items-center gap-1.5 transition-all text-sm font-medium"
                                    onClick={saveDraftAsFile}
                                >
                                    <Save size={14} />
                                    <span>Save Draft</span>
                                </button>
                            </div>

                            <div className="flex items-center gap-2 mr-4">
                                <span className="text-xs font-semibold text-gray-600 uppercase">Export:</span>
                                <button
                                    className="px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-md flex items-center gap-1.5 transition-all text-sm font-medium"
                                    onClick={exportRubricHTML}
                                >
                                    <FileText size={14} />
                                    <span>HTML</span>
                                </button>
                                <button
                                    className="px-3 py-1.5 bg-pink-100 hover:bg-pink-200 text-pink-700 rounded-md flex items-center gap-1.5 transition-all text-sm font-medium"
                                    onClick={exportRubricPDF}
                                >
                                    <FileText size={14} />
                                    <span>PDF</span>
                                </button>
                                <button
                                    className="px-3 py-1.5 bg-cyan-100 hover:bg-cyan-200 text-cyan-700 rounded-md flex items-center gap-1.5 transition-all text-sm font-medium"
                                    onClick={exportRubricTemplate}
                                >
                                    <FileText size={14} />
                                    <span>Template</span>
                                </button>
                                <button
                                    className="px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-md flex items-center gap-1.5 transition-all text-sm font-medium"
                                    onClick={exportRubricFinalJSON}
                                >
                                    <FileText size={14} />
                                    <span>Rubric JSON</span>
                                </button>
                            </div>

                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-600 uppercase">Tools:</span>
                                <button
                                    className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-md flex items-center gap-1.5 transition-all text-sm font-medium"
                                    onClick={resetPage}
                                >
                                    🔄 <span>Reset</span>
                                </button>
                                <button
                                    className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-md flex items-center gap-1.5 transition-all text-sm font-medium"
                                    title="Reverse rubric order"
                                    onClick={reverseRubricOrder}
                                >
                                    ⇅ <span>Reverse</span>
                                </button>
                            </div>
                        </div>

                        {pasteAccordionOpen && (
                            <div className="w-full mt-3 border border-indigo-200 rounded-lg bg-indigo-50/70 shadow-inner">
                                <div className="px-4 py-2 flex items-center justify-between border-b border-indigo-100 bg-indigo-100/60 rounded-t-lg">
                                    <div className="flex items-center gap-2 text-sm font-semibold text-indigo-800">
                                        <FileText size={16} />
                                        <span>Paste Rubric JSON</span>
                                    </div>
                                    <button
                                        className="p-1 text-indigo-700 hover:text-indigo-900"
                                        onClick={() => setPasteAccordionOpen(false)}
                                        aria-label="Close paste JSON"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="p-4 space-y-3">
                                    <textarea
                                        value={pasteJSONText}
                                        onChange={(e) => setPasteJSONText(e.target.value)}
                                        className="w-full h-32 p-3 text-sm border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none bg-white"
                                        placeholder="Paste rubric JSON here"
                                    />
                                    <div className="flex items-center justify-end gap-2">
                                        <button
                                            className="px-4 py-2 text-sm text-indigo-700 bg-white border border-indigo-200 rounded-lg hover:bg-indigo-50"
                                            onClick={() => {
                                                setPasteJSONText('');
                                                setPasteAccordionOpen(false);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow"
                                            onClick={applyPastedJSON}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex items-center justify-between px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-6 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-700">Criteria:</span>
                                <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded font-bold">{rubricData.criteria.length}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-700">Levels:</span>
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded font-bold">{rubricData.rubricLevels.length}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-700">Groups:</span>
                                <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded font-bold">{rubricData.groups.length}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-700">Total Points:</span>
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-bold">{calculateTotalPoints()}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-700">Pass Threshold:</span>
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-bold">{rubricData.assignmentInfo.passingThreshold}%</span>
                            </div>
                        </div>
                        <div className="text-xs text-gray-600">
                            Not auto-saved — export the Rubric JSON to keep a copy before refreshing
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full px-6 py-6 grid grid-cols-12 gap-4">
                <div className="col-span-12 lg:col-span-3 space-y-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <button
                            onClick={() => toggleSection('assignment')}
                            className="w-full px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 flex items-center justify-between hover:from-blue-100 hover:to-blue-150 transition-all"
                        >
                            <div className="flex items-center gap-2">
                                <Book size={18} className="text-blue-600" />
                                <span className="font-semibold text-gray-800">Assignment Info</span>
                            </div>
                            {expandedSections.assignment ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>

                        {expandedSections.assignment && (
                            <div className="p-4 space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={rubricData.assignmentInfo.title}
                                        onChange={(e) => updateAssignmentInfo('title', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea
                                        value={rubricData.assignmentInfo.description}
                                        onChange={(e) => updateAssignmentInfo('description', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        rows="3"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Weight (%)</label>
                                        <input
                                            type="number"
                                            value={rubricData.assignmentInfo.weight}
                                            onChange={(e) => updateAssignmentInfo('weight', parseFloat(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Pass (%)</label>
                                        <input
                                            type="number"
                                            value={rubricData.assignmentInfo.passingThreshold}
                                            onChange={(e) => updateAssignmentInfo('passingThreshold', parseFloat(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <button
                            onClick={() => toggleSection('levels')}
                            className="w-full px-4 py-3 bg-gradient-to-r from-purple-50 to-purple-100 flex items-center justify-between"
                        >
                            <div className="flex items-center gap-2">
                                <Settings size={18} className="text-purple-600" />
                                <span className="font-semibold text-gray-800">Achievement Levels</span>
                            </div>
                            {expandedSections.levels ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>

                        {expandedSections.levels && (
                            <div className="p-4 space-y-3">
                                <div className="grid grid-cols-3 gap-2">
                                    {[2, 3, 4, 5, 6, 7].map((count) => (
                                    <button
                                        key={count}
                                        onClick={() => changeLevelCount(count)}
                                        type="button"
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${levelCount === count ? 'bg-purple-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                    >
                                        {count}
                                    </button>
                                ))}
                            </div>
                                <div className="space-y-2">
                                    {rubricData.rubricLevels.map((level) => (
                                        <div
                                            key={level.level}
                                            className="p-3 rounded-lg border space-y-2"
                                            style={{ backgroundColor: level.color + '10', borderColor: level.color + '40' }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: level.color }} />
                                                <input
                                                    type="text"
                                                    value={level.name}
                                                    onChange={(e) => updateLevelName(level.level, e.target.value)}
                                                    className="w-full text-sm font-medium px-2 py-1 rounded border border-gray-200 focus:ring-2 focus:ring-purple-400"
                                                    style={{ color: level.color }}
                                                    placeholder="Level name"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="card">
                        <div className="card-header teal">
                            <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                                <span>📁</span> Criteria Groups
                            </h3>
                            <button onClick={addNewGroup} className="p-1 bg-teal-600 text-white rounded" aria-label="Add group">
                                <Plus size={16} />
                            </button>
                        </div>
                        <div className="card-content">
                            <div className="groups-container">
                                {rubricData.groups.map((group) => (
                                    <GroupWrapper
                                        key={group.id}
                                        group={group}
                                        criteria={getCriteriaForGroup(group.id)}
                                        isActive={hasActiveCriterion(group.id)}
                                        isExpanded={expandedGroups[group.id] !== false}
                                        onToggleExpand={handleGroupToggleExpand}
                                        onSelectGroup={handleGroupSelect}
                                        onEditGroup={handleEditGroup}
                                        onRenameGroup={handleRenameGroup}
                                        onDeleteGroup={deleteGroup}
                                        onDeleteCriterion={removeCriterion}
                                        activeCriterionId={activeCriterion}
                                        onSelectCriterion={handleCriterionSelect}
                                    />
                                ))}

                                {getUngroupedCriteria().length > 0 && (
                                    <div className="ungrouped-section">
                                        <div className="ungrouped-label">⊗ Ungrouped</div>
                                        {getUngroupedCriteria().map((criterion, index) => (
                                        <CriterionItem
                                            key={criterion.id}
                                            criterion={criterion}
                                            index={index}
                                            isActive={activeCriterion === criterion.id}
                                            onDelete={removeCriterion}
                                            onSelect={handleUngroupedCriterionSelect}
                                        />
                                    ))}
                                    </div>
                                )}
                            </div>

                            <button className="add-btn-full primary" onClick={addCriterionToActiveGroup} type="button">
                                + Add Criterion to Group
                            </button>
                            <button className="add-btn-full success" onClick={addNewGroup} type="button">
                                📁 Add New Group
                            </button>
                        </div>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-6">
                    <div className="bg-white rounded-xl shadow-sm border">
                        <div className="px-6 py-4 bg-gradient-to-r from-teal-50 to-blue-50 border-b">
                            <div className="flex items-center justify-between gap-4">
                                <h2 className="text-lg font-bold text-gray-800 mb-3">Criterion Details</h2>
                                <div className="flex items-center gap-2">
                                    <div className="criterion-nav-buttons">
                                        <button className="nav-btn" onClick={goToPreviousCriterion} disabled={!hasPreviousCriterion()}>
                                            <span>◄</span>
                                            <span>Prev</span>
                                        </button>
                                        <div className="criterion-counter">
                                            {getCurrentCriterionIndex() + 1} of {rubricData.criteria.length}
                                        </div>
                                        <button className="nav-btn" onClick={goToNextCriterion} disabled={!hasNextCriterion()}>
                                            <span>Next</span>
                                            <span>►</span>
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => exportCriterionJSON(activeCriterion)}
                                        className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 border border-blue-200"
                                        title="Export this criterion as JSON"
                                    >
                                        <Download size={14} /> Export
                                    </button>
                                    <button
                                        onClick={() => criterionImportInputRef.current?.click()}
                                        className="flex items-center gap-1 text-xs px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100 border border-green-200"
                                        title="Import a criterion from JSON"
                                    >
                                        <Upload size={14} /> Import
                                    </button>
                                    <button
                                        onClick={() => removeCriterion(activeCriterion)}
                                        className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                                    >
                                        <Trash2 size={16} /> Remove
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="group-selector-wrapper">
                                    <div className="group-selector-label">
                                        <span>📁</span>
                                        <span>Criterion Group Assignment</span>
                                    </div>
                                    <select
                                        className="form-control group-select"
                                        value={activeCriterionData.groupId || ''}
                                        onChange={(e) => updateCriterion(activeCriterion, 'groupId', e.target.value || null)}
                                    >
                                        <option value="">⊗ No Group (Ungrouped)</option>
                                        {rubricData.groups.map((group) => (
                                            <option key={group.id} value={group.id}>
                                                📁 {group.name || 'Untitled Group'}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="help-text">
                                        <span>ℹ️</span>
                                        <span>Assign this criterion to a group for better organization in the rubric</span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-sm font-semibold text-gray-700">Criterion Title</label>
                                    <p className="text-xs text-gray-500">Give this criterion a clear name that appears in the rubric and grading tool.</p>
                                    <input
                                        type="text"
                                        value={activeCriterionData.name}
                                        onChange={(e) => updateCriterion(activeCriterion, 'name', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        placeholder="Criterion name..."
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="block text-sm font-semibold text-gray-700">Criterion Description</label>
                                    <p className="text-xs text-gray-500">Describe what you expect students to demonstrate for this criterion.</p>
                                    <textarea
                                        value={activeCriterionData.description}
                                        onChange={(e) => updateCriterion(activeCriterion, 'description', e.target.value)}
                                        className="w-full px-3 py-2 border rounded-lg"
                                        rows="3"
                                        placeholder="Describe the criterion..."
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                        <label className="block text-sm font-semibold text-gray-700">Max Points</label>
                                        <p className="text-xs text-gray-500 min-h-[32px] leading-snug">Total points available for this criterion.</p>
                                        <input
                                            type="number"
                                            value={activeCriterionData.maxPoints ?? ''}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                updateCriterion(activeCriterion, 'maxPoints', raw === '' ? '' : parseFloat(raw));
                                            }}
                                            onBlur={(e) => {
                                                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                                                    updateCriterion(activeCriterion, 'maxPoints', 0);
                                                }
                                            }}
                                            className="w-full px-3 py-2 border rounded-lg"
                                            placeholder="Max points"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="block text-sm font-semibold text-gray-700">Weight (%)</label>
                                        <p className="text-xs text-gray-500 min-h-[32px] leading-snug">Press Enter to recalculate points from weight.</p>
                                        <input
                                            type="number"
                                            value={activeCriterionData.weight ?? ''}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                updateCriterion(activeCriterion, 'weight', raw === '' ? '' : parseFloat(raw));
                                            }}
                                            onBlur={(e) => {
                                                if (e.target.value !== '' && isNaN(parseFloat(e.target.value))) {
                                                    updateCriterion(activeCriterion, 'weight', '');
                                                }
                                            }}
                                            onKeyDown={handleWeightKeyDown}
                                            className="w-full px-3 py-2 border rounded-lg"
                                            placeholder="Weight %"
                                        />
                                    </div>
                                </div>
                                <div className="p-3 bg-gray-50 border rounded-lg">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800">Level point values</p>
                                            <p className="text-xs text-gray-600">Choose automatic multipliers or enter custom points per level.</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setCriterionPointMode(activeCriterion, true)}
                                                className={`px-3 py-1 rounded-md text-xs font-semibold border transition-all ${
                                                    activeCriterionData.useAutoPointValues
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                                }`}
                                            >
                                                Auto (multipliers)
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCriterionPointMode(activeCriterion, false)}
                                                className={`px-3 py-1 rounded-md text-xs font-semibold border transition-all ${
                                                    !activeCriterionData.useAutoPointValues
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                                }`}
                                            >
                                                Custom points
                                            </button>
                                        </div>
                                    </div>
                                    {!activeCriterionData.useAutoPointValues && (
                                        <p className="text-[11px] text-gray-600 mt-2">Adjust points below for each level. Blank values fallback to the auto-derived points.</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-6 space-y-3">
                            {activeLevels.map((level) => (
                                <CriterionLevelCell
                                    key={level.level}
                                    level={level}
                                    useAutoPointValues={activeCriterionData.useAutoPointValues}
                                    levelData={activeCriterionData.levels?.[level.level]}
                                    autoPoints={getLevelPoints(activeCriterionData, level.level)}
                                    onPointsChange={handleLevelPointsChange}
                                    onDescriptionChange={handleLevelDescriptionChange}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                        <div className="col-span-12 lg:col-span-3">
                            <div className="bg-white rounded-xl shadow-sm border sticky top-24">
                                <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-purple-100 border-b">
                                    <div className="flex items-center gap-2">
                                        <Sparkles size={18} className="text-purple-600" />
                                        <h3 className="font-semibold text-gray-800">Feedback Library</h3>
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">For: {activeCriterionData.name || 'Untitled Criterion'}</p>
                                </div>
                                <div className="px-4 pt-3 pb-1 flex flex-wrap gap-2 border-b border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => feedbackFileInputRef.current?.click()}
                                        className="flex-1 min-w-[140px] px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg text-xs font-semibold border border-purple-200 flex items-center justify-center gap-2 transition"
                                    >
                                        <Upload size={14} />
                                        Import Comments JSON
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleExportFeedbackLibrary}
                                        className="flex-1 min-w-[140px] px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-xs font-semibold border border-green-200 flex items-center justify-center gap-2 transition"
                                    >
                                        <Download size={14} />
                                        Export Comments JSON
                                    </button>
                                </div>
                                <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                                    <FeedbackSection
                                        title="Strengths"
                                        icon="💪"
                                        color="#27ae60"
                                        items={activeCriterionData.feedbackLibrary?.strengths || []}
                                        onAdd={(text) => handleAddFeedbackItem('strengths', text)}
                                        onRemove={(index) => handleRemoveFeedbackItem('strengths', index)}
                                        onUpdate={(index, text) => handleUpdateFeedbackItem('strengths', index, text)}
                                    />
                                    <FeedbackSection
                                        title="Improvements"
                                        icon="🔧"
                                        color="#f39c12"
                                        items={activeCriterionData.feedbackLibrary?.improvements || []}
                                        onAdd={(text) => handleAddFeedbackItem('improvements', text)}
                                        onRemove={(index) => handleRemoveFeedbackItem('improvements', index)}
                                        onUpdate={(index, text) => handleUpdateFeedbackItem('improvements', index, text)}
                                    />
                                    <FeedbackSection
                                        title="General"
                                        icon="💬"
                                        color="#2980b9"
                                        items={activeCriterionData.feedbackLibrary?.general || []}
                                        onAdd={(text) => handleAddFeedbackItem('general', text)}
                                        onRemove={(index) => handleRemoveFeedbackItem('general', index)}
                                        onUpdate={(index, text) => handleUpdateFeedbackItem('general', index, text)}
                                    />
                                </div>
                            </div>

                        </div>
                    </div>

            <div className="w-full px-6">
                <div className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl shadow-sm border border-indigo-200">
                    <div className="px-6 py-4 bg-gradient-to-r from-indigo-100 to-purple-100 border-b">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white text-lg">📊</span>
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">Overall Achievement Levels</h3>
                                    <p className="text-sm text-gray-600">Edit overall feedback comments for each performance level</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => overallAchievementFileInputRef.current?.click()}
                                    className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-semibold flex items-center gap-2 border border-indigo-200 shadow-sm"
                                >
                                    <Upload size={16} />
                                    <span>Import Overall JSON</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={exportOverallAchievementJSON}
                                    className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm"
                                >
                                    <Download size={16} />
                                    <span>Export Overall JSON</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={applyDefaultOverallDescriptions}
                                    className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm"
                                    title="Populate all Overall Achievement levels with generic default descriptions"
                                >
                                    <CheckCircle size={16} />
                                    <span>Apply Default Descriptions</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="flex items-center gap-2 flex-wrap mb-4">
                            <span className="text-xs font-semibold text-gray-600">Overall level count:</span>
                            {[2, 3, 4, 5, 6, 7].map((count) => (
                                <button
                                    key={count}
                                    onClick={() => changeOverallLevelCount(count)}
                                    type="button"
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                        overallLevelCount === count
                                            ? 'bg-indigo-600 text-white shadow-md'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {count}
                                </button>
                            ))}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {overallLevels.map((level) => (
                                <div key={level.level} className="bg-white rounded-lg border-2" style={{ borderColor: level.color + '60' }}>
                                    <div className="px-4 py-3" style={{ backgroundColor: level.color + '15' }}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: level.color }} />
                                                <span className="font-bold text-sm" style={{ color: level.color }}>
                                                    {level.name}
                                                </span>
                                            </div>
                                            <span className="text-xs text-gray-600">{(level.multiplier * 100).toFixed(0)}%</span>
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        <label className="block text-xs font-medium text-gray-600 mb-2">Overall Feedback:</label>
                                        <textarea
                                            value={level.description}
                                            onChange={(e) => updateOverallLevelDescription(level.level, e.target.value)}
                                            className="w-full text-xs text-gray-700 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 resize-y min-h-[96px]"
                                            rows="3"
                                            placeholder="Enter overall feedback for this level..."
                                        />
                                        <p className="text-xs text-gray-500 mt-2 italic">Appears in grading reports for this level</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-start gap-2">
                                <span className="text-blue-600 text-lg">💡</span>
                                <div>
                                    <p className="text-sm text-blue-900 font-medium">Tip: Overall Achievement Comments</p>
                                    <p className="text-xs text-blue-700 mt-1">These comments provide general feedback for each performance level and appear in grading reports.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isGroupModalOpen && groupModalDraft && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-gray-200">
                        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-amber-50 to-amber-100">
                            <div>
                                <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold">Group Settings</p>
                                <h3 className="text-lg font-bold text-gray-800">{groupModalDraft.name || 'Untitled Group'}</h3>
                            </div>
                            <button
                                type="button"
                                className="p-2 rounded-full hover:bg-white text-gray-600"
                                onClick={handleCloseGroupModal}
                                aria-label="Close group settings"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="form-section">
                                <label className="form-label">Group Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={groupModalDraft.name}
                                    onChange={(e) => handleGroupDraftChange('name', e.target.value)}
                                    placeholder="Group name..."
                                />
                            </div>

                            <div className="form-section">
                                <label className="form-label">Description (Optional)</label>
                                <textarea
                                    className="form-input"
                                    rows="3"
                                    value={groupModalDraft.description}
                                    onChange={(e) => handleGroupDraftChange('description', e.target.value)}
                                    placeholder="Group description..."
                                />
                            </div>

                            <div className="toggle-section grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <label className="toggle-option flex items-start gap-2">
                                    <input
                                        type="checkbox"
                                        checked={groupModalDraft.showTotals}
                                        onChange={(e) => handleGroupDraftChange('showTotals', e.target.checked)}
                                    />
                                    <span className="text-sm text-gray-700">Show group totals in rubric</span>
                                </label>
                                <label className="toggle-option flex items-start gap-2">
                                    <input
                                        type="checkbox"
                                        checked={groupModalDraft.collapseByDefault}
                                        onChange={(e) => handleGroupDraftChange('collapseByDefault', e.target.checked)}
                                    />
                                    <span className="text-sm text-gray-700">Collapse by default in grading</span>
                                </label>
                            </div>

                            <div className="form-section">
                                <label className="form-label">Levels for this group</label>
                                <div className="flex flex-wrap gap-2">
                                    {[2, 3, 4, 5, 6, 7].map((count) => (
                                        <button
                                            key={count}
                                            type="button"
                                            onClick={() => handleGroupLevelCountChange(count)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all ${
                                                groupModalDraft.levels?.length === count
                                                    ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:border-amber-400'
                                            }`}
                                        >
                                            {count} levels
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    This group currently uses {groupModalDraft.levels?.length || rubricData.rubricLevels.length}{' '}
                                    achievement levels. Criteria inside the group will align to this set.
                                </p>
                            </div>

                            <div className="grid grid-cols-3 gap-3 text-sm bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <div>
                                    <p className="text-gray-500 text-xs">Criteria</p>
                                    <p className="font-semibold text-gray-800">{getCriteriaForGroup(groupModalDraft.id).length}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500 text-xs">Total Points</p>
                                    <p className="font-semibold text-gray-800">{calculateGroupPoints(groupModalDraft.id)} pts</p>
                                </div>
                                <div>
                                    <p className="text-gray-500 text-xs">% of Rubric</p>
                                    <p className="font-semibold text-gray-800">{calculateGroupPercentage(groupModalDraft.id)}%</p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end gap-2 rounded-b-2xl">
                            <button
                                type="button"
                                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
                                onClick={handleCloseGroupModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-amber-500 hover:bg-amber-600 shadow-sm"
                                onClick={handleSaveGroupDetails}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RubricCreator;
